import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import ErrorBoundary from '../components/ErrorBoundary'
import { io } from 'socket.io-client'

import {
	KeyboardArrowDown as KeyboardArrowDownIcon,
	Search as SearchIcon,
} from '@mui/icons-material'

import {
	Button,
	TextField,
} from '@mui/material'

import DefaultModal from '../components/modal/DefaultModal/DefaultModal'

import dayjs from 'dayjs'

import { useNavigate, useSearchParams } from 'react-router-dom'
import { emitSnackbar } from "../lib/snackbar-emitter"

import {
	formatDateLabel,
	formatPhoneNumber,
	formatTimestampLabel,
	formatKwikMessageContent,
	getKwikMessageSearchText,
	getDefaultScoutDatePreset,
	getDefaultScoutDatePresetValue,
	getProtocolEditedMessageText,
	getProtocolMessagePayload,
	getScoutConversationExport,
	getPreferredAccount,
	getScoutMessageById,
	exportScoutConversationMessages,
	getScoutConversationExportEmailStatus,
	getScoutAccountAnalytics,
	getScoutAccountDetails,
	getScoutConversationAnalytics,
	getScoutContext,
	isScoutAccountActive,
	isDeletedProtocolMessagePayload,
	isEditedProtocolMessagePayload,
	listScoutDateOptions,
	listScoutAccountsPage,
	DEFAULT_SCOUT_ACCOUNTS_PAGE_SIZE,
	listScoutConversations,
	listScoutMessages,
	normalizeScoutDate,
	getScoutEnterprise,
	searchScoutMessages,
} from '../services/scout'
import { getSearchMessageSender, normalizeQueryText } from '../components/search/SearchHighlight'
import SearchResultsModal from '../components/search/SearchResultsModal'
import { useSnackbar } from '../components/SnackbarContext'
import {
	getVisibleConversations,
	isHumanReadableName,
	isPhoneLikeIdentifier,
	isTechnicalIdentifier,
	mergeDuplicateConversations,
} from './conversation-normalizer'
import ConversationSidebar from '../components/conversations/ConversationSidebar'
import ConversationOverview from '../components/conversations/ConversationOverview'
import ConversationInsightsSidebar from '../components/conversations/ConversationInsightsSidebar'
import ConversationProfileSidebar from '../components/conversations/ConversationProfileSidebar'
import ConversationThread from '../components/conversations/ConversationThread'
import {
	getHistoryEntryKey,
	getMessageIdentifier,
	getMessageKey,
	LinkifiedText,
} from '../components/conversations/ConversationMessageBody'

import { createHomeGraphicsViewModel } from '../components/home/graphics'

import HomeGraphicsSection from '../components/home/graphics'

import {
	getContactData,
	createContact,
	updateContact,
	resyncScoutContact,
} from "../services/scout"

import {
	renderCRMFeatures,
	renderExportConversation,
} from "../services/utils"

import { getGreetings } from "../utils"

const DEFAULT_CONVERSATION_LIMIT = 10
const CONVERSATION_FETCH_LIMIT = 20
const CONVERSATION_LIST_BOTTOM_THRESHOLD = 120
const DEFAULT_MESSAGE_LIMIT = 20
const TOP_LOAD_THRESHOLD = 72
const EXPORT_STATUS_POLL_INTERVAL = 3000
const EXPORT_STATUS_MAX_ATTEMPTS = 100
const EXPORT_STATUS_DONE = new Set(['SENT', 'FINISHED', 'COMPLETED'])
const EXPORT_STATUS_FAILED = new Set(['ERROR', 'FAILED'])

const wait = (milliseconds) => new Promise((resolve) => {
	window.setTimeout(resolve, milliseconds)
})

const normalizeExportStatus = (status) => String(status || '').trim().toUpperCase()

const waitForExportEmailStatus = async ({ accountId, statusKey }) => {
	for (let attempt = 0; attempt < EXPORT_STATUS_MAX_ATTEMPTS; attempt += 1) {
		const exportStatus = await getScoutConversationExportEmailStatus({ accountId, statusKey })
		const status = normalizeExportStatus(exportStatus?.status)

		if (EXPORT_STATUS_DONE.has(status)) {
			return exportStatus
		}

		if (EXPORT_STATUS_FAILED.has(status)) {
			throw new Error(exportStatus?.message || 'Não foi possível enviar o e-mail da exportação.')
		}

		await wait(EXPORT_STATUS_POLL_INTERVAL)
	}

	throw new Error('Não foi possível confirmar o envio do e-mail da exportação.')
}

const SOCKET_EVENTS = [
	'messages.upsert',
	'messages.update',
	'messages.delete',
	'contacts.upsert',
	'contacts.update',
	// 'chats.upsert',
	// 'chats.update',
	'groups.upsert',
	'groups.update',
	'group-participants.update'
]
const SOCKET_TRANSPORTS = ['websocket']
const SCOUT_WEBSOCKET_ENABLED = process.env.REACT_APP_SCOUT_USE_WEBSOCKET === 'true'
const MEDIA_MESSAGE_TYPES = new Set([
	'imageMessage',
	'videoMessage',
	'documentMessage',
	'documentWithCaptionMessage',
	'audioMessage',
	'stickerMessage',
])
const IGNORED_MESSAGE_TYPES = new Set([
	'encEventResponseMessage',
	'eventResponseMessage',
	'secretEncryptedMessage',
])
const IGNORED_THREAD_MESSAGE_TYPES = new Set([
	'encEventResponseMessage',
	'eventResponseMessage',
	'pollUpdateMessage',
])

const normalizeSocketUrl = (value) => {
	if (!value || typeof value !== 'string') {
		return null
	}

	return value.trim().replace(/\/+$/, '') || null
}

const normalizeSocketPath = (value) => {
	if (!value || typeof value !== 'string') {
		return '/socket.io'
	}

	const normalized = value.trim().replace(/\/+$/, '')
	if (!normalized) {
		return '/socket.io'
	}

	return normalized.startsWith('/') ? normalized : `/${normalized}`
}

const isInsecureSocketUrlForPage = (value) => {
	if (!value || window.location.protocol !== 'https:') {
		return false
	}

	try {
		const parsed = new URL(value)
		return parsed.protocol === 'http:'
	} catch {
		return false
	}
}

const getSocketConnectionConfig = () => {
	const candidates = [
		process.env.REACT_APP_WEBSOCKET_URL,
		process.env.REACT_APP_DEFAULT_EVO_API_URL,
		process.env.REACT_APP_HOST,
		window.location.origin,
	]
	const skippedInsecureUrls = []
	const seen = new Set()

	const urls = candidates
		.map(normalizeSocketUrl)
		.filter((value) => {
			if (!value || seen.has(value)) {
				return false
			}

			seen.add(value)

			if (isInsecureSocketUrlForPage(value)) {
				skippedInsecureUrls.push(value)
				return false
			}

			return true
		})

	return {
		urls,
		path: normalizeSocketPath(process.env.REACT_APP_WEBSOCKET_PATH),
		skippedInsecureUrls,
	}
}

const isRenderableMessage = (message) => {
	const messageType = String(message?.messageType || message?.messagetype || '')
	return !message?.type && messageType !== 'reactionMessage' && !IGNORED_MESSAGE_TYPES.has(messageType)
}

const getEditHistory = (message, messages = []) => {
	if (!message?.id || !Array.isArray(messages)) {
		return []
	}

	const messageId = getMessageIdentifier(message)
	const seenHistoryEntries = new Set()

	return messages
		.filter((entry) => {
			const protocolId = entry?.protocolId || entry?.protocol_id
			return Boolean(entry?.type && protocolId && String(protocolId) === messageId)
		})
		.filter((entry) => {
			const historyKey = getHistoryEntryKey(entry)
			if (seenHistoryEntries.has(historyKey)) {
				return false
			}

			seenHistoryEntries.add(historyKey)
			return true
		})
		.sort((first, second) => Number(first?.timestamp || 0) - Number(second?.timestamp || 0))
}

const getReactionTargetId = (message) => {
	const reactionPayload = message?.message
	if (!reactionPayload || typeof reactionPayload !== 'object') {
		return ''
	}

	return String(reactionPayload.reactedMessageId || reactionPayload.messageId || '')
}

const getReactionEmoji = (message) => {
	const reactionPayload = message?.message
	if (!reactionPayload || typeof reactionPayload !== 'object') {
		return ''
	}

	if (typeof reactionPayload.reaction === 'string') {
		return reactionPayload.reaction
	}

	return ''
}

const getReactionActorKey = (message) => {
	const reactionPayload = message?.message
	if (!reactionPayload || typeof reactionPayload !== 'object') {
		return getMessageIdentifier(message)
	}

	return String(reactionPayload.contact || reactionPayload.fromMe || getMessageIdentifier(message))
}

const buildMessageReactionLookup = (messages = []) => {
	const reactionsByMessageId = new Map()

	messages
		.filter((entry) => entry?.messageType === 'reactionMessage')
		.sort((first, second) => Number(first?.timestamp || 0) - Number(second?.timestamp || 0))
		.forEach((reactionMessage) => {
			const targetId = getReactionTargetId(reactionMessage)
			if (!targetId) {
				return
			}

			const actorKey = getReactionActorKey(reactionMessage)
			const emoji = getReactionEmoji(reactionMessage)
			const currentReactions = reactionsByMessageId.get(targetId) || new Map()

			if (emoji) {
				currentReactions.set(actorKey, {
					id: `${targetId}-${actorKey}`,
					emoji,
					fromMe: Boolean(reactionMessage?.message?.fromMe),
					timestamp: Number(reactionMessage?.timestamp || 0),
				})
			} else {
				currentReactions.delete(actorKey)
			}

			if (currentReactions.size) {
				reactionsByMessageId.set(targetId, currentReactions)
			} else {
				reactionsByMessageId.delete(targetId)
			}
		})

	return reactionsByMessageId
}

const validateEmail = (value = '') => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value).trim())

const toDateTimeLocal = (value) => {
	return dayjs(value).format('YYYY-MM-DDTHH:mm')
}

const getConversationTitle = (conversation) => {
	if (!conversation) {
		return 'Conversa'
	}

	const kwikContactName = normalizeTitleText(conversation.kwik_contact_name)
	const conversationName = normalizeTitleText(conversation.name)

	if (isHumanReadableName(kwikContactName)) {
		return kwikContactName
	}

	if (isHumanReadableName(conversationName)) {
		return conversationName
	}

	return kwikContactName || conversationName || formatPhoneNumber(conversation.phone_num)
}

const GENERIC_CONVERSATION_TITLES = new Set([
	'contato',
	'conversa',
	'grupo',
	'participante',
	'sem nome',
	'sem numero identificado',
	'sem número identificado',
])

const normalizeTitleText = (value) => String(value || '').trim()

const getConversationTitleQuality = (value) => {
	const normalized = normalizeTitleText(value)
	if (!normalized || GENERIC_CONVERSATION_TITLES.has(normalized.toLowerCase())) {
		return 0
	}

	if (isTechnicalIdentifier(normalized)) {
		return 0
	}

	if (isPhoneLikeIdentifier(normalized)) {
		return 1
	}

	return isHumanReadableName(normalized) ? 3 : 2
}

const getBestConversationTitle = (...values) => {
	return values.reduce((best, value) => {
		const normalized = normalizeTitleText(value)
		const bestQuality = getConversationTitleQuality(best)
		const nextQuality = getConversationTitleQuality(normalized)

		return nextQuality > bestQuality ? normalized : best
	}, '')
}

const getRealtimeChatName = (value) => {
	if (!value || typeof value !== 'object') {
		return ''
	}

	return getBestConversationTitle(
		value?.name,
		value?.pushName,
		value?.pushname,
		value?.subject,
		value?.notify,
		value?.verifiedName,
		value?.displayName,
		value?.profileName,
		value?.contactName,
		value?.chatName,
		value?.groupName,
		value?.metadata?.subject,
		value?.groupMetadata?.subject,
	)
}

const getRealtimeContactName = (value) => {
	if (!value || typeof value !== 'object') {
		return ''
	}

	return getBestConversationTitle(
		value?.kwik_contact_name,
		value?.pushName,
		value?.pushname,
		value?.name,
		value?.notify,
		value?.verifiedName,
		value?.displayName,
		value?.profileName,
		value?.contactName,
	)
}

const isAbortError = (error) => error?.name === 'AbortError'

const mergeMessages = (olderMessages, currentMessages) => {
	const mergedMap = new Map()

	;[...olderMessages, ...currentMessages].forEach((message, index) => {
		mergedMap.set(getMessageKey(message, index), message)
	})

	return Array.from(mergedMap.values()).sort((first, second) => {
		const firstValue = normalizeComparableTimestamp(first?.timestamp)
		const secondValue = normalizeComparableTimestamp(second?.timestamp)

		return firstValue - secondValue
	})
}

const shouldIgnoreThreadMessage = (message) => {
	return IGNORED_THREAD_MESSAGE_TYPES.has(String(message?.messageType || message?.messagetype || ''))
}

const filterThreadMessages = (messageList = []) => {
	if (!Array.isArray(messageList)) {
		return []
	}

	return messageList.filter((message) => !shouldIgnoreThreadMessage(message))
}

const getConversationHistoryPreviewFromMessages = (messageList = []) => {
	const historyByProtocolId = new Map()
	let latestVisibleMessage = null
	let latestHistoryVisibleMessage = null

	messageList.forEach((message) => {
		if (message?.type === 'DELETED' || message?.type === 'EDITED') {
			const protocolId = String(message?.protocolId || message?.protocol_id || '').trim()
			if (protocolId) {
				const currentHistory = historyByProtocolId.get(protocolId) || []
				historyByProtocolId.set(protocolId, [...currentHistory, message])
			}
			return
		}

		if (!isRenderableMessage(message)) {
			return
		}

		if (!latestVisibleMessage || normalizeComparableTimestamp(message?.timestamp) >= normalizeComparableTimestamp(latestVisibleMessage?.timestamp)) {
			latestVisibleMessage = message
		}
	})

	if (!latestVisibleMessage) {
		return null
	}

	messageList.forEach((message) => {
		if (!isRenderableMessage(message)) {
			return
		}

		const historyMessages = historyByProtocolId.get(getMessageIdentifier(message)) || []
		if (!historyMessages.length) {
			return
		}

		const visibleTimestamp = normalizeComparableTimestamp(message?.timestamp)
		historyMessages.forEach((historyMessage) => {
			const historyTimestamp = normalizeComparableTimestamp(historyMessage?.timestamp)
			const latestTimestamp = Math.max(historyTimestamp, visibleTimestamp)

			if (!latestHistoryVisibleMessage || latestTimestamp >= latestHistoryVisibleMessage.latestTimestamp) {
				latestHistoryVisibleMessage = {
					message,
					historyMessage,
					latestTimestamp,
				}
			}
		})
	})

	if (!latestHistoryVisibleMessage) {
		return null
	}

	const latestVisibleTimestamp = normalizeComparableTimestamp(latestVisibleMessage?.timestamp)
	if (latestHistoryVisibleMessage.latestTimestamp < latestVisibleTimestamp) {
		return null
	}

	if (latestHistoryVisibleMessage.historyMessage?.type === 'DELETED') {
		return {
			type: 'DELETED',
			message: 'Mensagem apagada',
			protocolId: latestHistoryVisibleMessage.historyMessage?.protocolId || latestHistoryVisibleMessage.message?.id || '',
			timestamp: latestHistoryVisibleMessage.latestTimestamp,
		}
	}

	return {
		...latestHistoryVisibleMessage.historyMessage,
		type: 'EDITED',
		protocolId: latestHistoryVisibleMessage.historyMessage?.protocolId || latestHistoryVisibleMessage.message?.id || '',
		timestamp: latestHistoryVisibleMessage.latestTimestamp,
	}
}

const NON_EDITABLE_CONVERSATION_JID_SUFFIXES = new Set(['broadcast', 'newsletter'])
const NON_EDITABLE_CONVERSATION_JIDS = new Set(['0@s.whatsapp.net'])

const isEditableContactConversation = (conversation) => {
	if (!conversation || conversation.type !== 'CONTACT') {
		return false
	}

	const remoteJid = String(conversation?.id || conversation?._id || '').trim()
	if (NON_EDITABLE_CONVERSATION_JIDS.has(remoteJid)) {
		return false
	}

	const [, suffix = ''] = remoteJid.split('@')

	return !NON_EDITABLE_CONVERSATION_JID_SUFFIXES.has(suffix)
}

const getConversationIdVariants = (conversation) => {
	const ids = [
		conversation?.id,
		conversation?._id,
		...(Array.isArray(conversation?.alternateIds) ? conversation.alternateIds : []),
	]

	return Array.from(new Set(ids.map((id) => String(id || '').trim()).filter(Boolean)))
}

const findConversationByAnyId = (conversationList = [], conversationId = '') => {
	const normalizedConversationId = String(conversationId || '').trim()
	if (!normalizedConversationId) {
		return null
	}

	return conversationList.find((conversation) => {
		return getConversationIdVariants(conversation).includes(normalizedConversationId)
	}) || null
}

const normalizeTimestamp = (value) => {
	if (typeof value === 'number') {
		return Number.isFinite(value) ? value : 0
	}

	if (typeof value === 'string') {
		const numericValue = Number(value)
		return Number.isFinite(numericValue) ? numericValue : 0
	}

	if (value && typeof value === 'object') {
		const low = Number(value.low || 0)
		const high = Number(value.high || 0)

		if (Number.isFinite(low) && Number.isFinite(high)) {
			return high * (2 ** 32) + low
		}
	}

	return 0
}

const normalizeComparableTimestamp = (value) => {
	const normalizedValue = normalizeTimestamp(value)
	if (!normalizedValue) {
		return 0
	}

	return normalizedValue > 9999999999 ? Math.trunc(normalizedValue / 1000) : normalizedValue
}

const parseConversationIdentity = (value) => {
	const remoteJid = String(value || '').trim()
	if (!remoteJid) {
		return {
			remoteJid: '',
			phoneNumber: '',
			type: 'CONTACT',
		}
	}

	const [phoneNumber = '', suffix = ''] = remoteJid.split('@')

	return {
		remoteJid,
		phoneNumber,
		type: suffix === 'g.us' ? 'GROUP' : 'CONTACT',
	}
}

const getSocketDataItems = (payload) => {
	if (Array.isArray(payload?.data)) {
		return payload.data
	}

	if (payload?.data) {
		return [payload.data]
	}

	return []
}

const getRealtimeMessageRemoteJid = (message) => {
	return String(
		message?.key?.remoteJid
		|| message?.remoteJid
		|| message?.source
		|| message?.message?.reactionMessage?.key?.remoteJid
		|| message?.id
		|| '',
	).trim()
}

const getRealtimeDeleteKey = (message) => {
	if (message?.key && typeof message.key === 'object') {
		return message.key
	}

	return message && typeof message === 'object' ? message : {}
}

const getRealtimeDeleteMessageId = (message) => {
	const deleteKey = getRealtimeDeleteKey(message)

	return String(
		deleteKey?.id
			|| message?.keyId
			|| message?.messageId
			|| message?.chatMessageId
			|| '',
	).trim()
}

const getRealtimeDeleteRemoteJid = (message) => {
	const deleteKey = getRealtimeDeleteKey(message)

	return String(
		deleteKey?.remoteJid
			|| message?.remoteJid
			|| message?.source
			|| '',
	).trim()
}

const normalizeRealtimeEventTimestamp = (rawTimestamp, rawDateTime) => {
	const normalizedTimestamp = normalizeComparableTimestamp(rawTimestamp)
	if (normalizedTimestamp) {
		return normalizedTimestamp
	}

	const normalizedDateTime = dayjs(rawDateTime)
	if (normalizedDateTime.isValid()) {
		return normalizedDateTime.unix()
	}

	return dayjs().unix()
}

const normalizeParticipantScalar = (value) => {
	if (typeof value === 'string' || typeof value === 'number') {
		return String(value).trim()
	}

	return ''
}

const buildRealtimeReactionPayload = (message) => {
	const reactionPayload = message?.message?.reactionMessage || {}
	const fromMe = Boolean(message?.key?.fromMe)
	const reactedMessageId = reactionPayload?.key?.id || ''
	const conversation = reactionPayload?.key?.remoteJid || message?.key?.remoteJid || ''
	const contact = fromMe
		? conversation
		: message?.key?.participant || message?.key?.remoteJid || ''

	return {
		reaction: reactionPayload?.text || '',
		fromMe,
		contact,
		reactedMessageId,
		conversation,
	}
}

const getRealtimeMessageType = (message) => message?.messageType || message?.messagetype || ''

const normalizePhoneDigits = (value = '') => String(value || '').replace(/\D+/g, '')

const getRemoteJidPhoneDigits = (value = '') => {
	const [phoneNumber = ''] = String(value || '').split('@')
	return normalizePhoneDigits(phoneNumber)
}

const isExplicitFalse = (value) => value === false || value === 0 || value === 'false' || value === '0'

const isSelfAddressedProtocolEvent = ({ remoteJid, connectedPhone, pushName, fromMe }) => {
	const remotePhone = getRemoteJidPhoneDigits(remoteJid)
	const accountPhone = normalizePhoneDigits(connectedPhone)

	return Boolean(
		remotePhone
			&& accountPhone
			&& remotePhone === accountPhone
			&& !String(pushName || '').trim()
			&& isExplicitFalse(fromMe),
	)
}

const isIgnoredSelfAddressedProtocolMessage = (message, connectedPhone) => {
	if (!message || typeof message !== 'object') {
		return false
	}

	const messageType = getRealtimeMessageType(message)
	if (!['protocolMessage', 'REVOKE', 'editedMessage'].includes(messageType)) {
		return false
	}

	return isSelfAddressedProtocolEvent({
		remoteJid: getRealtimeMessageRemoteJid(message),
		connectedPhone,
		pushName: message?.pushName,
		fromMe: message?.key?.fromMe ?? message?.fromMe,
	})
}

const isIgnoredSelfAddressedRealtimeEvent = (eventName, item, connectedPhone) => {
	if (!item || typeof item !== 'object') {
		return false
	}

	if (eventName === 'messages.delete') {
		const deleteKey = getRealtimeDeleteKey(item)

		return isSelfAddressedProtocolEvent({
			remoteJid: getRealtimeDeleteRemoteJid(item),
			connectedPhone,
			pushName: item?.pushName,
			fromMe: deleteKey?.fromMe ?? item?.fromMe,
		})
	}

	return eventName === 'messages.upsert' && isIgnoredSelfAddressedProtocolMessage(item, connectedPhone)
}

const hasProtocolPreview = (conversation) => {
	const message = conversation?.message

	return conversation?.message_type === 'protocolMessage'
		|| conversation?.message_type === 'editedMessage'
		|| message?.type === 'DELETED'
		|| message?.type === 'EDITED'
}

const isIgnoredSelfAddressedProtocolConversation = (conversation, connectedPhone) => {
	if (!conversation || typeof conversation !== 'object' || !hasProtocolPreview(conversation)) {
		return false
	}

	const remoteJid = conversation?.remoteJid || conversation?.remote_jid || conversation?.id || conversation?._id
	const pushName = conversation?.pushName || conversation?.push_name || conversation?.sender

	return isSelfAddressedProtocolEvent({
		remoteJid,
		connectedPhone,
		pushName,
		fromMe: conversation?.fromMe ?? conversation?.from_me,
	})
}

const normalizeRealtimeGroupParticipantsUpdate = (rawUpdate, envelope = {}) => {
	if (!rawUpdate || typeof rawUpdate !== 'object') {
		return null
	}

	const remoteJid = String(rawUpdate?.id || '').trim()
	if (!remoteJid) {
		return null
	}

	const participantsSource = rawUpdate?.participants
		?? rawUpdate?.participant
		?? rawUpdate?.members
		?? rawUpdate?.member
		?? rawUpdate?.users
		?? rawUpdate?.user
		?? rawUpdate?.recipient
		?? rawUpdate?.recipients
	const participants = (Array.isArray(participantsSource) ? participantsSource : participantsSource ? [participantsSource] : [])
		.map((participant) => {
			if (participant && typeof participant === 'object') {
				return normalizeParticipantScalar(
					participant?.jid
					|| participant?.id
					|| participant?.participant
					|| participant?.participant_jid
					|| participant?.participant_lid
					|| participant?.phoneNumber
					|| participant?.phone_number
					|| participant?.participant_phone
					|| '',
				)
			}

			return normalizeParticipantScalar(participant)
		})
		.filter(Boolean)
	const participantsDataSource = rawUpdate?.participantsData ?? rawUpdate?.participants_data ?? rawUpdate?.participantData
	const participantsData = (Array.isArray(participantsDataSource) ? participantsDataSource : participantsDataSource ? [participantsDataSource] : [])
		.filter((participant) => participant && typeof participant === 'object')
	const timestamp = normalizeRealtimeEventTimestamp(
		rawUpdate?.timestamp || rawUpdate?.messageTimestamp,
		rawUpdate?.dateTime || rawUpdate?.date_time || envelope?.date_time,
	)
	const participantsKey = (participantsData.length
		? participantsData.map((participant) => normalizeParticipantScalar(
			participant?.participant_jid
			|| participant?.participant_lid
			|| participant?.jid
			|| participant?.id
			|| participant?.phoneNumber
			|| participant?.phone_number
			|| participant?.participant_phone
			|| '',
		)).filter(Boolean)
		: participants.map((participant) => normalizeParticipantScalar(participant)).filter(Boolean)
	).join(',')

	return {
		timestamp,
		fromMe: false,
		messageType: 'groupParticipantsUpdate',
		simplified_type: 'group_participants_update',
		source: remoteJid,
		pushName: rawUpdate?.pushName || rawUpdate?.push_name || rawUpdate?.sender || '',
		id: `group-participants.update:${remoteJid}:${rawUpdate?.action || ''}:${rawUpdate?.author || rawUpdate?.authorPn || ''}:${participantsKey}:${timestamp}`,
		message: {
			...rawUpdate,
			participants,
			participantsData,
		},
	}
}

const getPollUpdateCreationMessageId = (message) => {
	return String(
		message?.message?.pollUpdateMessage?.pollCreationMessageKey?.id
		|| message?.pollCreationMessageKey?.id
		|| message?.keyId
		|| '',
	).trim()
}

const getRealtimePollUpdates = (message) => {
	if (Array.isArray(message?.pollUpdates)) {
		return message.pollUpdates
	}

	if (Array.isArray(message?.message?.pollUpdates)) {
		return message.message.pollUpdates
	}

	return null
}

const isMessageMatchingPollCreation = (message, pollCreationMessageId) => {
	if (!pollCreationMessageId) {
		return false
	}

	return [
		getMessageIdentifier(message),
		message?.id,
		message?.chatMessageId,
		message?.messageId,
		message?.key?.id,
		message?.message?.key?.id,
	].some((candidate) => String(candidate || '') === pollCreationMessageId)
}

const applyRealtimePollUpdateToMessages = (currentMessages = [], rawMessage) => {
	const pollCreationMessageId = getPollUpdateCreationMessageId(rawMessage)
	const pollUpdates = getRealtimePollUpdates(rawMessage)

	if (!pollCreationMessageId || !Array.isArray(pollUpdates)) {
		return currentMessages
	}

	return currentMessages.map((message) => isMessageMatchingPollCreation(message, pollCreationMessageId)
		? { ...message, pollUpdates }
		: message)
}

const getEventResponseCreationMessageId = (message) => {
	return String(
		message?.eventCreationMessageKey?.id
		|| message?.message?.eventCreationMessageKey?.id
		|| message?.message?.encEventResponseMessage?.eventCreationMessageKey?.id
		|| message?.message?.eventResponseMessage?.eventCreationMessageKey?.id
		|| message?.keyId
		|| '',
	).trim()
}

const getRealtimeEventResponses = (message) => {
	if (Array.isArray(message?.eventResponses)) {
		return message.eventResponses
	}

	if (Array.isArray(message?.message?.eventResponses)) {
		return message.message.eventResponses
	}

	if (String(message?.status || '').toUpperCase() === 'EVENT_RESPONSE' && Array.isArray(message?.pollUpdates)) {
		return message.pollUpdates
	}

	return null
}

const isMessageMatchingEventCreation = (message, eventCreationMessageId) => {
	if (!eventCreationMessageId) {
		return false
	}

	return [
		getMessageIdentifier(message),
		message?.id,
		message?.chatMessageId,
		message?.messageId,
		message?.key?.id,
		message?.message?.key?.id,
	].some((candidate) => String(candidate || '') === eventCreationMessageId)
}

const getEventResponseIdentifier = (response, index) => {
	if (!response || typeof response !== 'object') {
		return `response-${index}`
	}

	return String(
		response.id
		|| response.jid
		|| response.responderJid
		|| response.remoteJid
		|| `response-${index}`,
	).trim()
}

const mergeEventResponses = (currentResponses = [], incomingResponses = []) => {
	const responseMap = new Map()

	const appendResponses = (responses = []) => {
		if (!Array.isArray(responses)) {
			return
		}

		responses.forEach((response, index) => {
			const identifier = getEventResponseIdentifier(response, index)
			responseMap.set(identifier, {
				...(responseMap.get(identifier) || {}),
				...response,
			})
		})
	}

	appendResponses(currentResponses)
	appendResponses(incomingResponses)

	return Array.from(responseMap.values())
}

const applyRealtimeEventResponseToMessages = (currentMessages = [], rawMessage) => {
	const eventCreationMessageId = getEventResponseCreationMessageId(rawMessage)
	const eventResponses = getRealtimeEventResponses(rawMessage)

	if (!eventCreationMessageId || !Array.isArray(eventResponses)) {
		return currentMessages
	}

	return currentMessages.map((message) => isMessageMatchingEventCreation(message, eventCreationMessageId)
		? { ...message, eventResponses: mergeEventResponses(message?.eventResponses, eventResponses) }
		: message)
}

const normalizeRealtimeMessage = (rawMessage) => {
	if (!rawMessage || typeof rawMessage !== 'object') {
		return null
	}

	const messageType = rawMessage?.messageType || rawMessage?.messagetype || ''
	const payload = rawMessage?.message && typeof rawMessage.message === 'object' ? rawMessage.message : {}
	const baseMessage = {
		timestamp: normalizeTimestamp(rawMessage?.messageTimestamp || rawMessage?.timestamp),
		fromMe: Boolean(rawMessage?.key?.fromMe ?? rawMessage?.fromMe),
		messageType,
		source: getRealtimeMessageRemoteJid(rawMessage),
		pushName: rawMessage?.pushName || '',
		id: rawMessage?.key?.id || rawMessage?.id || rawMessage?.chatMessageId || '',
		isForwarded: Boolean(rawMessage?.contextInfo?.isForwarded),
		restricted: rawMessage?.restricted,
		transcription: rawMessage?.transcription,
		transcription_error: rawMessage?.transcription_error,
		transcription_status: rawMessage?.transcription_status,
		status: rawMessage?.status,
	}

	if (!messageType || IGNORED_MESSAGE_TYPES.has(messageType)) {
		return null
	}

	if (messageType === 'conversation') {
		return {
			...baseMessage,
			message: payload?.conversation || '',
			simplified_type: 'text',
		}
	}

	if (messageType === 'reactionMessage') {
		return {
			...baseMessage,
			message: buildRealtimeReactionPayload(rawMessage),
		}
	}

	if (['protocolMessage', 'REVOKE', 'editedMessage'].includes(messageType)) {
		const protocolMessage = getProtocolMessagePayload(payload)
		const editedText = getProtocolEditedMessageText(payload)
		const isEditedMessage = isEditedProtocolMessagePayload(payload)
		const isDeletedMessage = messageType === 'REVOKE' || isDeletedProtocolMessagePayload(payload)

		if (!isEditedMessage && !isDeletedMessage) {
			return null
		}

		return {
			...baseMessage,
			type: isEditedMessage ? 'EDITED' : 'DELETED',
			message: editedText,
			protocolId: protocolMessage?.key?.id || protocolMessage?.key?.oldid || '',
			messageType: 'protocolMessage',
		}
	}

	if (MEDIA_MESSAGE_TYPES.has(messageType) || messageType === 'ptvMessage') {
		const resolvedType = messageType === 'ptvMessage' ? 'videoMessage' : messageType
		const mediaPayload = payload?.[resolvedType] || payload?.[messageType] || payload || {}
		const fileLength = typeof mediaPayload?.fileLength === 'object'
			? normalizeTimestamp(mediaPayload.fileLength)
			: Number(mediaPayload?.fileLength || mediaPayload?.fileSize || 0)

		return {
			...baseMessage,
			messageType: resolvedType,
			message: {
				...mediaPayload,
				base64: mediaPayload?.base64 || payload?.base64 || '',
				mediaUrl: mediaPayload?.mediaUrl || payload?.mediaUrl || '',
			},
			caption: mediaPayload?.caption || '',
			title: mediaPayload?.title || mediaPayload?.fileName || '',
			fileName: mediaPayload?.fileName || '',
			mimetype: mediaPayload?.mimetype || '',
			size: fileLength,
			thumbnail: mediaPayload?.jpegThumbnail || '',
			gifPlayback: Boolean(mediaPayload?.gifPlayback),
		}
	}

	if (messageType === 'contactMessage') {
		const contactPayload = payload?.contactMessage || payload || {}
		return {
			...baseMessage,
			...contactPayload,
			contactMessage: contactPayload,
			message: contactPayload,
			simplified_type: 'contact',
		}
	}

	if (messageType === 'locationMessage' || messageType === 'liveLocationMessage') {
		const locationPayload = payload?.[messageType] || payload || {}
		return {
			...baseMessage,
			...locationPayload,
			[messageType]: locationPayload,
			message: locationPayload,
			thumbnail: locationPayload?.jpegThumbnail || '',
			simplified_type: 'location',
		}
	}

	if (messageType === 'eventMessage') {
		const eventPayload = payload?.eventMessage || payload || {}
		return {
			...baseMessage,
			...eventPayload,
			eventMessage: eventPayload,
			message: eventPayload,
			simplified_type: 'event',
		}
	}

	if (messageType === 'pollCreationMessageV3') {
		const pollPayload = payload?.pollCreationMessageV3 || payload || {}
		return {
			...baseMessage,
			...pollPayload,
			pollCreationMessageV3: pollPayload,
			message: pollPayload,
			pollUpdates: Array.isArray(rawMessage?.pollUpdates) ? rawMessage.pollUpdates : undefined,
			simplified_type: 'poll_creaction_message_v3',
		}
	}

	return {
		...baseMessage,
		message: payload,
	}
}

const normalizeRealtimeDeletedMessage = (rawMessage) => {
	if (!rawMessage || typeof rawMessage !== 'object') {
		return null
	}

	const deleteKey = getRealtimeDeleteKey(rawMessage)
	const protocolId = getRealtimeDeleteMessageId(rawMessage)

	if (!protocolId) {
		return null
	}

	return {
		timestamp: normalizeTimestamp(rawMessage?.messageTimestamp || rawMessage?.timestamp),
		fromMe: Boolean(deleteKey?.fromMe ?? rawMessage?.fromMe),
		messageType: 'protocolMessage',
		source: getRealtimeDeleteRemoteJid(rawMessage),
		pushName: rawMessage?.pushName || '',
		id: `${protocolId}:deleted`,
		key: deleteKey,
		type: 'DELETED',
		message: '',
		protocolId,
		status: 'DELETED',
		participant: deleteKey?.participant || rawMessage?.participant,
	}
}

const sortConversationsByLastMessage = (conversationList = []) => {
	return [...conversationList].sort((first, second) => {
		return normalizeTimestamp(second?.last_message_timestamp) - normalizeTimestamp(first?.last_message_timestamp)
	})
}

const upsertConversation = (conversationList, incomingConversation) => {
	const conversationId = String(incomingConversation?.id || '').trim()
	if (!conversationId) {
		return conversationList
	}

	const conversationMap = new Map(
		conversationList.flatMap((conversation) => {
			const ids = [
				conversation?.id,
				...(Array.isArray(conversation?.alternateIds) ? conversation.alternateIds : []),
			].map((id) => String(id || '').trim()).filter(Boolean)

			return ids.map((id) => [id, conversation])
		}),
	)
	const existingConversation = conversationMap.get(conversationId) || null
	const bestName = isHumanReadableName(incomingConversation?.kwik_contact_name)
		? normalizeTitleText(incomingConversation?.kwik_contact_name)
		: getBestConversationTitle(
			existingConversation?.kwik_contact_name,
			incomingConversation?.kwik_contact_name,
			existingConversation?.name,
			incomingConversation?.name,
		)
	const bestKwikContactName = getBestConversationTitle(
		existingConversation?.kwik_contact_name,
		incomingConversation?.kwik_contact_name,
	)
	const mergedConversation = {
		...existingConversation,
		...incomingConversation,
		id: conversationId,
		_id: incomingConversation?._id || existingConversation?._id || conversationId,
		name: bestName || incomingConversation?.name || existingConversation?.name || '',
		kwik_contact_name: bestKwikContactName || incomingConversation?.kwik_contact_name || existingConversation?.kwik_contact_name,
		avatarUrl:
			incomingConversation?.avatarUrl
			|| incomingConversation?.profile_picture
			|| existingConversation?.avatarUrl
			|| existingConversation?.profile_picture
			|| '',
		profile_picture:
			incomingConversation?.profile_picture
			|| incomingConversation?.avatarUrl
			|| existingConversation?.profile_picture
			|| existingConversation?.avatarUrl
			|| '',
	}

	;[
		conversationId,
		...(Array.isArray(mergedConversation?.alternateIds) ? mergedConversation.alternateIds : []),
	].forEach((id) => {
		if (id) {
			conversationMap.set(String(id), mergedConversation)
		}
	})

	return mergeDuplicateConversations(sortConversationsByLastMessage(Array.from(new Set(conversationMap.values()))))
}

const preserveResolvedConversationNames = (incomingConversations = [], existingConversations = []) => {
	const existingById = new Map(
		existingConversations.flatMap((conversation) => {
			const ids = [
				conversation?.id,
				...(Array.isArray(conversation?.alternateIds) ? conversation.alternateIds : []),
			].map((id) => String(id || '').trim()).filter(Boolean)

			return ids.map((id) => [id, conversation])
		}),
	)

	return mergeDuplicateConversations(incomingConversations.map((conversation) => {
		const existingConversation = existingById.get(String(conversation?.id || ''))
		if (!existingConversation) {
			return conversation
		}

		const bestName = isHumanReadableName(conversation?.kwik_contact_name)
			? normalizeTitleText(conversation?.kwik_contact_name)
			: getBestConversationTitle(
				existingConversation?.kwik_contact_name,
				conversation?.kwik_contact_name,
				existingConversation?.name,
				conversation?.name,
			)
		const bestKwikContactName = getBestConversationTitle(
			existingConversation?.kwik_contact_name,
			conversation?.kwik_contact_name,
		)

		return {
			...conversation,
			name: bestName || conversation?.name,
			kwik_contact_name: bestKwikContactName || conversation?.kwik_contact_name,
		}
	}))
}

const buildConversationFromRealtimeMessage = ({ message, existingConversation, selectedConversationId }) => {
	const remoteJid = getRealtimeMessageRemoteJid(message)
	if (!remoteJid) {
		return null
	}

	const normalizedMessage = normalizeRealtimeMessage(message)
	if (!normalizedMessage) {
		return null
	}

	const isDeletedRealtimeMessage = normalizedMessage.type === 'DELETED'
	const isEditedRealtimeMessage = normalizedMessage.type === 'EDITED'
	const conversationPreviewMessage = isDeletedRealtimeMessage
		? {
			type: 'DELETED',
			message: 'Mensagem apagada',
			protocolId: normalizedMessage.protocolId || '',
		}
		: isEditedRealtimeMessage
		? normalizedMessage
		: normalizedMessage.message

	const identity = parseConversationIdentity(remoteJid)
	const isSelectedConversation = String(selectedConversationId || '') === remoteJid
	const currentUnread = Number(
		existingConversation?.unreadMessages
			?? existingConversation?.unread_messages
			?? existingConversation?.unreadCount
			?? 0,
	)
	const nextUnread = normalizedMessage.fromMe || isSelectedConversation
		? 0
		: currentUnread + 1
	const incomingName = identity.type === 'GROUP'
		? getBestConversationTitle(getRealtimeChatName(message), existingConversation?.name)
		: getBestConversationTitle(
			existingConversation?.kwik_contact_name,
			existingConversation?.name,
			identity.phoneNumber,
		)

	return {
		...existingConversation,
		id: remoteJid,
		_id: remoteJid,
		name: incomingName || existingConversation?.name || identity.phoneNumber,
		owner: message?.instanceId || existingConversation?.owner,
		type: existingConversation?.type || identity.type,
		phone_num: existingConversation?.phone_num || identity.phoneNumber,
		message: conversationPreviewMessage,
		message_type: normalizedMessage.messageType,
		last_message_timestamp: normalizedMessage.timestamp || existingConversation?.last_message_timestamp || 0,
		fromMe: normalizedMessage.fromMe,
		profile_picture: existingConversation?.profile_picture || '',
		sender: normalizedMessage.pushName || existingConversation?.sender || '',
		kwik_contact_id: existingConversation?.kwik_contact_id,
		kwik_contact_name: existingConversation?.kwik_contact_name,
		instancename: existingConversation?.instancename || message?.instance,
		unreadMessages: nextUnread,
	}
}

const buildConversationFromRealtimeContact = ({ contact, existingConversation }) => {
	const remoteJid = String(contact?.remoteJid || contact?.id || '').trim()
	if (!remoteJid) {
		return null
	}

	const identity = parseConversationIdentity(remoteJid)
	const contactName = getRealtimeContactName(contact)
	const incomingName = getBestConversationTitle(
		contact?.kwik_contact_name,
		contactName,
		existingConversation?.kwik_contact_name,
		existingConversation?.name,
		identity.phoneNumber,
	)
	const profilePicture = contact?.profilePicUrl || contact?.profile_picture || contact?.avatarUrl || existingConversation?.profile_picture || ''

	return {
		...existingConversation,
		id: remoteJid,
		_id: remoteJid,
		name: incomingName || existingConversation?.name || identity.phoneNumber,
		owner: contact?.instanceId || existingConversation?.owner,
		type: existingConversation?.type || identity.type,
		phone_num: existingConversation?.phone_num || identity.phoneNumber,
		profile_picture: profilePicture,
		avatarUrl: profilePicture || existingConversation?.avatarUrl || '',
		sender: existingConversation?.sender || contact?.pushName || '',
		kwik_contact_id: contact?.kwik_contact_id || existingConversation?.kwik_contact_id,
		kwik_contact_name: contact?.kwik_contact_name || existingConversation?.kwik_contact_name,
		instancename: existingConversation?.instancename || contact?.instance,
		last_message_timestamp: existingConversation?.last_message_timestamp || 0,
		unreadMessages: Number(existingConversation?.unreadMessages ?? existingConversation?.unread_messages ?? 0),
	}
}

const buildConversationFromRealtimeDelete = ({ message, existingConversation, selectedConversationId }) => {
	const remoteJid = getRealtimeDeleteRemoteJid(message)
	if (!remoteJid) {
		return null
	}

	const normalizedMessage = normalizeRealtimeDeletedMessage(message)
	if (!normalizedMessage) {
		return null
	}

	const identity = parseConversationIdentity(remoteJid)
	const isSelectedConversation = String(selectedConversationId || '') === remoteJid
	const currentUnread = Number(
		existingConversation?.unreadMessages
			?? existingConversation?.unread_messages
			?? existingConversation?.unreadCount
			?? 0,
	)
	const nextUnread = normalizedMessage.fromMe || isSelectedConversation
		? 0
		: currentUnread + 1

	return {
		...existingConversation,
		id: remoteJid,
		_id: remoteJid,
		name: existingConversation?.name || identity.phoneNumber,
		owner: message?.instanceId || existingConversation?.owner,
		type: existingConversation?.type || identity.type,
		phone_num: existingConversation?.phone_num || identity.phoneNumber,
		message: {
			type: 'DELETED',
			message: 'Mensagem apagada',
			protocolId: normalizedMessage.protocolId || '',
		},
		message_type: 'protocolMessage',
		last_message_timestamp: normalizedMessage.timestamp || existingConversation?.last_message_timestamp || 0,
		fromMe: normalizedMessage.fromMe,
		profile_picture: existingConversation?.profile_picture || '',
		sender: existingConversation?.sender || '',
		kwik_contact_id: existingConversation?.kwik_contact_id,
		kwik_contact_name: existingConversation?.kwik_contact_name,
		instancename: existingConversation?.instancename || message?.instance,
		unreadMessages: nextUnread,
	}
}

const buildConversationFromRealtimeGroupParticipantsUpdate = ({ update, existingConversation, selectedConversationId, payload }) => {
	const remoteJid = String(update?.id || '').trim()
	if (!remoteJid) {
		return null
	}

	const normalizedMessage = normalizeRealtimeGroupParticipantsUpdate(update, payload)
	if (!normalizedMessage) {
		return null
	}

	const identity = parseConversationIdentity(remoteJid)
	const isSelectedConversation = String(selectedConversationId || '') === remoteJid
	const currentUnread = Number(
		existingConversation?.unreadMessages
			?? existingConversation?.unread_messages
			?? existingConversation?.unreadCount
			?? 0,
	)
	const nextUnread = isSelectedConversation ? 0 : currentUnread + 1

	return {
		...existingConversation,
		id: remoteJid,
		_id: remoteJid,
		name: existingConversation?.name || identity.phoneNumber,
		owner: existingConversation?.owner,
		type: existingConversation?.type || identity.type,
		phone_num: existingConversation?.phone_num || identity.phoneNumber,
		message: normalizedMessage.message,
		message_type: normalizedMessage.messageType,
		last_message_timestamp: normalizedMessage.timestamp || existingConversation?.last_message_timestamp || 0,
		fromMe: false,
		profile_picture: existingConversation?.profile_picture || '',
		sender: existingConversation?.sender || '',
		kwik_contact_id: existingConversation?.kwik_contact_id,
		kwik_contact_name: existingConversation?.kwik_contact_name,
		instancename: existingConversation?.instancename || payload?.instance,
		unreadMessages: nextUnread,
	}
}

const buildConversationFromRealtimeChat = ({ chat, existingConversation }) => {
	const remoteJid = String(chat?.remoteJid || chat?.id || '').trim()
	if (!remoteJid) {
		return null
	}

	const identity = parseConversationIdentity(remoteJid)
	const incomingName = getBestConversationTitle(
		existingConversation?.kwik_contact_name,
		existingConversation?.name,
		getRealtimeChatName(chat),
		identity.phoneNumber,
	)

	return {
		...existingConversation,
		id: remoteJid,
		_id: remoteJid,
		name: incomingName || existingConversation?.name || identity.phoneNumber,
		owner: chat?.instanceId || existingConversation?.owner,
		type: existingConversation?.type || identity.type,
		phone_num: existingConversation?.phone_num || identity.phoneNumber,
		profile_picture: existingConversation?.profile_picture || '',
		kwik_contact_id: existingConversation?.kwik_contact_id,
		kwik_contact_name: existingConversation?.kwik_contact_name,
		instancename: existingConversation?.instancename,
		unreadMessages: Number(chat?.unreadMessages ?? existingConversation?.unreadMessages ?? 0),
		last_message_timestamp: existingConversation?.last_message_timestamp || 0,
	}
}

const Conversations = () => {
	const navigate = useNavigate()
	const [searchParams, setSearchParams] = useSearchParams()
	const initialDateOptions = listScoutDateOptions()
	const initialSelectedPreset = searchParams.get('period') || getDefaultScoutDatePreset()
	const initialSelectedOption = initialDateOptions.find((option) => option.key === initialSelectedPreset) || initialDateOptions[0]
	const { user } = getScoutContext()
	const { showSnackbar } = useSnackbar()
	const threadRef = useRef(null)
	const messagesLoadingRef = useRef(false)
	const conversationsLoadingRef = useRef(false)
	const conversationViewVersionRef = useRef(0)
	const activeThreadRequestRef = useRef({ controller: null, version: 0 })
	const selectedConversationIdRef = useRef(searchParams.get('conversationId') || '')
	const selectedConversationIdsRef = useRef([searchParams.get('conversationId') || ''].filter(Boolean))
	const threadNearBottomRef = useRef(true)
	const pendingHistoryScrollAdjustmentRef = useRef(null)
	const suppressThreadScrollHandlerRef = useRef(false)
	const dateDropdownRef = useRef(null)
	const [accounts, setAccounts] = useState([])
	const [selectedAccountId, setSelectedAccountId] = useState(searchParams.get('accountId') || '')
	const [selectedConversationId, setSelectedConversationId] = useState(searchParams.get('conversationId') || '')
	const [dateValue, setDateValue] = useState(() => normalizeScoutDate(searchParams.get('date') || initialSelectedOption?.endDate || getDefaultScoutDatePresetValue()))
	const [selectedDatePreset, setSelectedDatePreset] = useState(initialSelectedPreset)
	const [isDateMenuOpen, setIsDateMenuOpen] = useState(false)
	const [anchorMessageId, setAnchorMessageId] = useState(searchParams.get('anchorMessageId') || '')
	const [searchValue, setSearchValue] = useState('')
	const [searchResultsOpen, setSearchResultsOpen] = useState(false)
	const [searchResultsLoading, setSearchResultsLoading] = useState(false)
	const [searchResults, setSearchResults] = useState([])
	const [searchFeedback, setSearchFeedback] = useState('')
	const searchDebounceTimerRef = useRef(null)
	const [analytics, setAnalytics] = useState(null)
	const [conversationAnalytics, setConversationAnalytics] = useState(null)
	const [conversations, setConversations] = useState([])
	const [conversationCursor, setConversationCursor] = useState(0)
	const [conversationFetchCursor, setConversationFetchCursor] = useState(0)
	const [hasMoreRemoteConversations, setHasMoreRemoteConversations] = useState(false)
	const [messages, setMessages] = useState([])
	const [messageCursor, setMessageCursor] = useState(0)
	const [initialMessagesLoading, setInitialMessagesLoading] = useState(false)
	const [messagesLoading, setMessagesLoading] = useState(false)
	const [loadingMediaIds, setLoadingMediaIds] = useState({})
	const [exportDialogOpen, setExportDialogOpen] = useState(false)
	const [exportEmail, setExportEmail] = useState('')
	const [exportType, setExportType] = useState('pdf')
	const [exportDateStart, setExportDateStart] = useState(toDateTimeLocal(dayjs().subtract(7, 'day').hour(8).minute(0)))
	const [exportDateEnd, setExportDateEnd] = useState(toDateTimeLocal(dayjs().hour(18).minute(0)))
	const [exportLoading, setExportLoading] = useState(false)
	const [exportFeedback, setExportFeedback] = useState('')
	const [rightSidebar, setRightSidebar] = useState(window.innerWidth >= 600 ? "insights" : null)
	const [mobileChartOpen, setMobileChartOpen] = useState(null)
	const [loading, setLoading] = useState(true)
	const [threadNearBottom, setThreadNearBottom] = useState(true)
	const [pendingNewMessagesCount, setPendingNewMessagesCount] = useState(0)
	const socketRef = useRef(null)

	const [enterprise, setEnterprise] = useState(null)
	const [optionsAnchorEl, setOptionsAnchorEl] = useState(null)
	const [contact, setContact] = useState(null)

	const frozenNamesRef = useRef(new Map())

	const getStableConversationTitle = useCallback((conversation) => {
		if (!conversation?.id) return 'Conversa'

		const kwikContactName = normalizeTitleText(conversation.kwik_contact_name)
		if (isHumanReadableName(kwikContactName)) {
			frozenNamesRef.current.set(conversation.id, kwikContactName)
			return kwikContactName
		}

		const existing = frozenNamesRef.current.get(conversation.id)
		const resolved = getConversationTitle(conversation)
		const contactName = normalizeTitleText(conversation.kwik_contact_name)

		if (isHumanReadableName(contactName)) {
			frozenNamesRef.current.set(conversation.id, contactName)
			return contactName
		}

		const existingQuality = getConversationTitleQuality(existing)
		const resolvedQuality = getConversationTitleQuality(resolved)

		if (existing && existingQuality >= resolvedQuality) {
			return existing
		}

		if (resolved && resolved.trim() && resolvedQuality > 0) {
			frozenNamesRef.current.set(conversation.id, resolved)
		}

		return resolved || existing || 'Conversa'
	}, [])

	const baseDateOptions = useMemo(() => listScoutDateOptions(), [])
	
	const selectedDateOption = useMemo(() => {
		return baseDateOptions.find((option) => option.key === selectedDatePreset) || baseDateOptions[0] || null
	}, [baseDateOptions, selectedDatePreset])
	
	const selectedDateRange = useMemo(() => {
		if (selectedDateOption) {
			return {
				startDate: selectedDateOption.startDate || selectedDateOption.value || dateValue,
				endDate: selectedDateOption.endDate || selectedDateOption.value || dateValue,
			}
		}

		return {
			startDate: dateValue,
			endDate: dateValue,
		}
	}, [dateValue, selectedDateOption])

	const selectedDateLabel = useMemo(() => {
		return selectedDateOption?.label || formatDateLabel(dateValue)
	}, [dateValue, selectedDateOption])

	const abortManagedRequest = useCallback((requestRef) => {
		if (requestRef.current?.controller) {
			requestRef.current.controller.abort()
			requestRef.current.controller = null
		}
	}, [])

	const resetConversationLoadingState = useCallback(() => {
		messagesLoadingRef.current = false
		pendingHistoryScrollAdjustmentRef.current = null
		suppressThreadScrollHandlerRef.current = false
		setInitialMessagesLoading(false)
		setMessagesLoading(false)
	}, [])

	const cancelConversationRequests = useCallback(() => {
		conversationViewVersionRef.current += 1
		abortManagedRequest(activeThreadRequestRef)
		resetConversationLoadingState()
	}, [abortManagedRequest, resetConversationLoadingState])

	const resetConversationViewState = useCallback(() => {
		pendingHistoryScrollAdjustmentRef.current = null
		suppressThreadScrollHandlerRef.current = false
		setMessages([])
		setConversationAnalytics(null)
		setMessageCursor(0)
		setPendingNewMessagesCount(0)
		setLoadingMediaIds({})
		setContact(null)
	}, [])

	const selectConversation = useCallback((conversationId, { accountId, anchorId = '' } = {}) => {
		if (selectedConversationId === conversationId)
			return

		cancelConversationRequests()
		resetConversationViewState()

		if (typeof accountId !== 'undefined' && accountId !== null) {
			setSelectedAccountId(String(accountId))
		}

		if (!conversationId) {
			setRightSidebar(null)
			setOptionsAnchorEl(null)
		}

		setSelectedConversationId(conversationId ? String(conversationId) : '')
		setAnchorMessageId(anchorId ? String(anchorId) : '')
	}, [cancelConversationRequests, resetConversationViewState, selectedConversationId])

	useEffect(() => {
		const selectedConversationRecord = findConversationByAnyId(conversations, selectedConversationId)
		const selectedIds = getConversationIdVariants(selectedConversationRecord || { id: selectedConversationId })

		selectedConversationIdsRef.current = selectedIds.length ? selectedIds : [selectedConversationId].filter(Boolean)
	}, [conversations, selectedConversationId])

	const optionsOpen = Boolean(optionsAnchorEl)

	useEffect(() => {
		selectedConversationIdRef.current = selectedConversationId
	}, [selectedConversationId])

	useEffect(() => {
		threadNearBottomRef.current = threadNearBottom
	}, [threadNearBottom])

	useLayoutEffect(() => {
		const pendingAdjustment = pendingHistoryScrollAdjustmentRef.current
		if (!pendingAdjustment || !threadRef.current) {
			return
		}

		if (pendingAdjustment.version !== conversationViewVersionRef.current) {
			pendingHistoryScrollAdjustmentRef.current = null
			return
		}

		const nextScrollHeight = threadRef.current.scrollHeight
		const scrollHeightDelta = nextScrollHeight - pendingAdjustment.previousScrollHeight

		suppressThreadScrollHandlerRef.current = true
		threadRef.current.scrollTop = pendingAdjustment.previousScrollTop + scrollHeightDelta
		pendingHistoryScrollAdjustmentRef.current = null

		window.requestAnimationFrame(() => {
			suppressThreadScrollHandlerRef.current = false
		})
	}, [messages])

	useEffect(() => {
		let isMounted = true

		const loadAccounts = async () => {
			setLoading(true)

			try {
				const [accountsResult, enterpriseResult] = await Promise.allSettled([
					listScoutAccountsPage({ page: 1, pageSize: DEFAULT_SCOUT_ACCOUNTS_PAGE_SIZE }),
					getScoutEnterprise(),
				])

				if (!isMounted) {
					return
				}

				if (enterpriseResult.status === "fulfilled") {
					setEnterprise(enterpriseResult.value)
				}

				let nextAccounts = accountsResult.value?.results || []

				if (
					selectedAccountId
					&& !nextAccounts.some((account) => String(account.id) === String(selectedAccountId))
				) {
					const selectedAccountDetails = await getScoutAccountDetails({ accountId: selectedAccountId, live: true })
					if (selectedAccountDetails?.id) {
						nextAccounts = [selectedAccountDetails, ...nextAccounts]
					}
				}

				setAccounts(nextAccounts)
			} catch (nextError) {

			} finally {
				if (isMounted) {
					setLoading(false)
				}
			}
		}

		loadAccounts()

		return () => {
			isMounted = false
		}
	}, [selectedAccountId])

	useEffect(() => {
		if (selectedAccountId || !accounts.length) {
			return
		}

		const preferredAccount = getPreferredAccount(accounts)

		if (preferredAccount) {
			setSelectedAccountId(String(preferredAccount.id))
		}
	}, [accounts, selectedAccountId])

	useEffect(() => {
		if (!isDateMenuOpen) {
			return undefined
		}

		const handlePointerDown = (event) => {
			if (!dateDropdownRef.current?.contains(event.target)) {
				setIsDateMenuOpen(false)
			}
		}

		const handleEscape = (event) => {
			if (event.key === 'Escape') {
				setIsDateMenuOpen(false)
			}
		}

		document.addEventListener('mousedown', handlePointerDown)
		document.addEventListener('keydown', handleEscape)

		return () => {
			document.removeEventListener('mousedown', handlePointerDown)
			document.removeEventListener('keydown', handleEscape)
		}
	}, [isDateMenuOpen])

	const executeSearch = async (query) => {
		const trimmed = String(query || '').trim()
		if (trimmed.length < 3) {
			setSearchResultsOpen(false)
			setSearchResults([])
			setSearchFeedback('')
			return
		}

		setSearchResultsOpen(true)
		setSearchResultsLoading(true)
		setSearchFeedback('')

		try {
			const normalizedSearch = normalizeQueryText(trimmed)
			const response = await searchScoutMessages({ text: normalizedSearch })
			const rawResults = Array.isArray(response?.data)
				? response.data
				: Array.isArray(response)
				? response
				: Object.values(response || {})

			const searchResultsNormalized = rawResults.map((entry, index) => {
				const conversationName = normalizeQueryText(entry?.conversationName || '')
				const senderName = normalizeQueryText(getSearchMessageSender(entry?.messages?.[0]))
				const messageMatched = (entry?.messages || []).some((message) => {
					const textContent = normalizeQueryText(getKwikMessageSearchText(message))
					return textContent.includes(normalizedSearch)
				})

				return {
					...entry,
					_searchOrder: index,
					_searchMatchedLocally: conversationName.includes(normalizedSearch) || senderName.includes(normalizedSearch) || messageMatched,
				}
			}).sort((first, second) => {
				const localMatchDiff = Number(Boolean(second._searchMatchedLocally)) - Number(Boolean(first._searchMatchedLocally))
				if (localMatchDiff) {
					return localMatchDiff
				}

				return Number(first._searchOrder || 0) - Number(second._searchOrder || 0)
			})

			setSearchResults(searchResultsNormalized)
			setSearchFeedback(searchResultsNormalized.length ? '' : 'Nenhum resultado encontrado para a busca.')
		} catch (error) {
			setSearchResults([])
			setSearchFeedback('Não foi possível buscar mensagens, tente novamente.')
		} finally {
			setSearchResultsLoading(false)
		}
	}

	const handleCloseSearchResults = () => {
		setSearchResultsOpen(false)
		setSearchResults([])
		setSearchFeedback('')
	}

	useEffect(() => {
		if (searchDebounceTimerRef.current) {
			clearTimeout(searchDebounceTimerRef.current)
		}

		const trimmed = String(searchValue || '').trim()

		if (trimmed.length < 3) {
			setSearchResultsOpen(false)
			setSearchResults([])
			setSearchFeedback('')
			return
		}

		searchDebounceTimerRef.current = setTimeout(() => {
			executeSearch(trimmed)
		}, 1000)

		return () => {
			if (searchDebounceTimerRef.current) {
				clearTimeout(searchDebounceTimerRef.current)
			}
		}
	}, [searchValue])

	useEffect(() => {
		const normalizedDate = normalizeScoutDate(dateValue)

		if (normalizedDate !== dateValue) {
			setDateValue(normalizedDate)
		}
	}, [dateValue])

	useEffect(() => {
		if (selectedDateOption?.endDate && selectedDateOption.endDate !== dateValue) {
			setDateValue(selectedDateOption.endDate)
		}
	}, [dateValue, selectedDateOption])

	const handleConversationListScroll = useCallback((event) => {
		if (conversationCursor === -1 && !hasMoreRemoteConversations) {
			return
		}

		const target = event.currentTarget
		const distanceFromBottom = target.scrollHeight - (target.scrollTop + target.clientHeight)

		if (distanceFromBottom <= CONVERSATION_LIST_BOTTOM_THRESHOLD) {
			if (conversationCursor !== -1 && conversationCursor < conversations.length) {
				setConversationCursor((currentCursor) => {
					if (currentCursor === -1) {
						return -1
					}

					const nextCursor = currentCursor + CONVERSATION_FETCH_LIMIT
					return nextCursor < conversations.length ? nextCursor : hasMoreRemoteConversations ? conversations.length : -1
				})
				return
			}

			if (hasMoreRemoteConversations && !conversationsLoadingRef.current) {
				conversationsLoadingRef.current = true
				listScoutConversations({
					accountId: selectedAccountId,
					limit: CONVERSATION_FETCH_LIMIT,
					skip: conversationFetchCursor,
					sort: 'desc',
				})
					.then((nextConversations) => {
						setConversations((currentConversations) => {
							const mergedConversations = mergeDuplicateConversations([...currentConversations, ...nextConversations])
							return preserveResolvedConversationNames(mergedConversations, currentConversations)
						})
						setConversationFetchCursor((currentCursor) => currentCursor + nextConversations.length)
						setHasMoreRemoteConversations(nextConversations.length >= CONVERSATION_FETCH_LIMIT)
						setConversationCursor((currentCursor) => {
							if (!nextConversations.length) {
								return -1
							}

							return currentCursor === -1 ? DEFAULT_CONVERSATION_LIMIT : currentCursor + CONVERSATION_FETCH_LIMIT
						})
					})
					.catch((nextError) => {

					})
					.finally(() => {
						conversationsLoadingRef.current = false
					})
			}
		}
	}, [conversationCursor, conversationFetchCursor, conversations.length, hasMoreRemoteConversations, selectedAccountId])


	useEffect(() => {
		if (!selectedAccountId) {
			setConversations([])
			setConversationCursor(0)
			setConversationFetchCursor(0)
			setHasMoreRemoteConversations(false)
			conversationsLoadingRef.current = false
			setAnalytics(null)
			return
		}

		let isMounted = true
		setLoading(true)
		setConversationCursor(0)
		setConversationFetchCursor(0)
		setHasMoreRemoteConversations(false)
		conversationsLoadingRef.current = false
		
		Promise.all([
			getScoutAccountAnalytics({
				accountId: selectedAccountId,
				dateStart: selectedDateRange.startDate,
				dateEnd: selectedDateRange.endDate,
			}),
			listScoutConversations({
				accountId: selectedAccountId,
				limit: DEFAULT_CONVERSATION_LIMIT,
				skip: 0,
				sort: 'desc',
			}),
		])
			.then(([nextAnalytics, nextConversations]) => {
				if (!isMounted) {
					return
				}

				setAnalytics(nextAnalytics)
				const mergedConversations = mergeDuplicateConversations(nextConversations)
				setConversations((currentConversations) => (
					preserveResolvedConversationNames(mergedConversations, currentConversations)
				))
				setConversationCursor(nextConversations.length >= DEFAULT_CONVERSATION_LIMIT ? DEFAULT_CONVERSATION_LIMIT : -1)
				setConversationFetchCursor(nextConversations.length)
				setHasMoreRemoteConversations(nextConversations.length >= DEFAULT_CONVERSATION_LIMIT)
				setSelectedConversationId((currentConversationId) => {
					const matchingConversation = mergedConversations.find(
						(conversation) => conversation.id === currentConversationId
							|| conversation.alternateIds?.includes(currentConversationId),
					)

					if (matchingConversation) {
						return matchingConversation.id
					}

					if (currentConversationId) {
						return currentConversationId
					}

					return currentConversationId
				})
			})
			.catch((nextError) => {

			})
			.finally(() => {
				if (isMounted) {
					setLoading(false)
				}
			})

		return () => {
			isMounted = false
		}
	}, [selectedAccountId, selectedDateRange.endDate, selectedDateRange.startDate])

	const selectedAccount = useMemo(() => {
		return accounts.find((account) => String(account.id) === String(selectedAccountId)) || null
	}, [accounts, selectedAccountId])

	useEffect(() => {
		if (!selectedAccountId) {
			return undefined
		}

		let isMounted = true

		getScoutAccountDetails({ accountId: selectedAccountId, live: true })
			.then((liveAccount) => {
				if (!isMounted || !liveAccount?.id) {
					return
				}

				setAccounts((currentAccounts) => currentAccounts.map((account) => {
					return String(account.id) === String(liveAccount.id)
						? { ...account, ...liveAccount }
						: account
				}))
			})
			.catch((error) => {
				console.warn('Falha ao atualizar dados do número Scout', error)
			})

		return () => {
			isMounted = false
		}
	}, [selectedAccountId])

	const selectedAccountAvatarClassName = useMemo(() => {
		return isScoutAccountActive(selectedAccount)
			? 'scout-account-card__avatar scout-account-card__avatar--online'
			: 'scout-account-card__avatar'
	}, [selectedAccount])

	const applyRealtimeConversationEvent = useCallback((eventName, payload) => {
		const items = getSocketDataItems(payload)
		if (!items.length) {
			return
		}

		const connectedPhone = selectedAccount?.phone_number

		setConversations((currentConversations) => {
			let nextConversations = currentConversations

			items.forEach((item) => {
				if (isIgnoredSelfAddressedRealtimeEvent(eventName, item, connectedPhone)) {
					return
				}

				if (eventName === 'messages.update') {
					return
				}

				const existingConversation = findConversationByAnyId(nextConversations, item?.remoteJid || item?.id || item?.key?.remoteJid)
				if ((eventName === 'contacts.upsert' || eventName === 'contacts.update') && !existingConversation) {
					return
				}

				const nextConversation = eventName === 'messages.upsert'
					? buildConversationFromRealtimeMessage({
						message: item,
						existingConversation,
						selectedConversationId: selectedConversationIdRef.current,
					})
					: eventName === 'messages.delete'
					? buildConversationFromRealtimeDelete({
						message: item,
						existingConversation,
						selectedConversationId: selectedConversationIdRef.current,
					})
					: eventName === 'contacts.upsert' || eventName === 'contacts.update'
					? buildConversationFromRealtimeContact({
						contact: item,
						existingConversation,
					})
					: eventName === 'group-participants.update'
					? buildConversationFromRealtimeGroupParticipantsUpdate({
						update: item,
						existingConversation,
						selectedConversationId: selectedConversationIdRef.current,
						payload,
					})
					: buildConversationFromRealtimeChat({
						chat: item,
						existingConversation,
					})

				// console.log('SCOUT LOG CONVERSATION RESOLUTION DEBUG', JSON.stringify({
				// 	eventName,
				// 	incomingRemoteJid: item?.key?.remoteJid || item?.remoteJid || item?.id,
				// 	incomingRemoteJidAlt: item?.key?.remoteJidAlt || item?.remoteJidAlt,
				// 	resolvedId: nextConversation?.id,
				// 	existingConversationId: existingConversation?.id,
				// 	currentConversationIds: nextConversations.map((conversation) => conversation.id),
				// }, null, 2))

				if (nextConversation) {
					nextConversations = upsertConversation(nextConversations, nextConversation)
				}
			})

			return nextConversations
		})
	}, [selectedAccount?.phone_number])

	const applyRealtimeFocusedConversationEvent = useCallback((eventName, payload) => {
		const items = getSocketDataItems(payload)
		if (!items.length) {
			return
		}

		const connectedPhone = selectedAccount?.phone_number

		items.forEach((item) => {
			if (isIgnoredSelfAddressedRealtimeEvent(eventName, item, connectedPhone)) {
				return
			}

			const remoteJid = eventName === 'group-participants.update'
				? String(item?.id || '').trim()
				: eventName === 'messages.delete'
				? getRealtimeDeleteRemoteJid(item)
				: getRealtimeMessageRemoteJid(item)
			if (!remoteJid || !selectedConversationIdsRef.current.includes(remoteJid)) {
				return
			}

			if (
				(
					eventName === 'messages.upsert'
					&& getRealtimeMessageType(item) === 'pollUpdateMessage'
				)
				|| (
					eventName === 'messages.update'
					&& String(item?.status || '').toUpperCase() === 'POLL_UPDATE'
				)
			) {
				setMessages((currentMessages) => (
					applyRealtimePollUpdateToMessages(currentMessages, item)
				))
				return
			}

			if (
				(
					eventName === 'messages.upsert'
					&& ['encEventResponseMessage', 'eventResponseMessage'].includes(getRealtimeMessageType(item))
				)
				|| (
					eventName === 'messages.update'
					&& String(item?.status || '').toUpperCase() === 'EVENT_RESPONSE'
				)
			) {
				setMessages((currentMessages) => (
					applyRealtimeEventResponseToMessages(currentMessages, item)
				))
				return
			}

			const normalizedMessage = eventName === 'group-participants.update'
				? normalizeRealtimeGroupParticipantsUpdate(item, payload)
				: eventName === 'messages.delete'
				? normalizeRealtimeDeletedMessage(item)
				: normalizeRealtimeMessage(item)
			if (!normalizedMessage) {
				return
			}

			setMessages((currentMessages) => {
				const currentRenderableIds = new Set(
					currentMessages.filter(isRenderableMessage).map((message) => getMessageIdentifier(message)),
				)
				const nextMessages = mergeMessages(currentMessages, [normalizedMessage])
				const isNewRenderableMessage = isRenderableMessage(normalizedMessage)
					&& !currentRenderableIds.has(getMessageIdentifier(normalizedMessage))

				if (isNewRenderableMessage) {
					if (threadNearBottomRef.current) {
						window.requestAnimationFrame(() => {
							if (threadRef.current) {
								threadRef.current.scrollTop = threadRef.current.scrollHeight
							}
						})
						setPendingNewMessagesCount(0)
					} else {
						setPendingNewMessagesCount((current) => current + 1)
					}
				}

				return nextMessages
			})
		})
	}, [selectedAccount?.phone_number])

	const handleSocketEvent = useCallback((eventName, payload) => {
		// console.log('SCOUT LOG SOCKET DEBUG', JSON.stringify({
		// 	eventName,
		// 	instance: payload?.instance,
		// 	items: getSocketDataItems(payload).map((item) => ({
		// 		messageId: item?.key?.id || item?.id,
		// 		messageType: item?.messageType,
		// 		remoteJid: item?.remoteJid,
		// 		remoteJidAlt: item?.remoteJidAlt,
		// 		keyRemoteJid: item?.key?.remoteJid,
		// 		keyRemoteJidAlt: item?.key?.remoteJidAlt,
		// 		source: item?.source,
		// 		pushName: item?.pushName,
		// 		fromMe: item?.key?.fromMe,
		// 	})),
		// }, null, 2))

		if (payload?.instance && payload.instance !== selectedAccount?.instance_name) {
			return
		}

		applyRealtimeConversationEvent(eventName, payload)

		if (eventName === 'messages.upsert' || eventName === 'messages.update' || eventName === 'messages.delete' || eventName === 'group-participants.update') {
			applyRealtimeFocusedConversationEvent(eventName, payload)
		}
	}, [applyRealtimeConversationEvent, applyRealtimeFocusedConversationEvent, selectedAccount?.instance_name])

	useEffect(() => {
		if (!SCOUT_WEBSOCKET_ENABLED) {
			return undefined
		}

		const { urls: websocketUrls, path: websocketPath, skippedInsecureUrls } = getSocketConnectionConfig()
		const wsApiKey = process.env.REACT_APP_DEFAULT_EVO_API_TOKEN

		if (skippedInsecureUrls.length) {
			console.warn('Scout websocket skipped insecure URL(s) on HTTPS page', skippedInsecureUrls)
		}

		if (!selectedAccountId || !selectedAccount?.instance_name || !wsApiKey || !websocketUrls.length) {
			return undefined
		}

		const [websocketUrl] = websocketUrls
		const namespaceUrl = `${websocketUrl}/${encodeURIComponent(selectedAccount.instance_name)}`

		const socketOptions = {
			transports: SOCKET_TRANSPORTS,
			path: websocketPath,
			auth: { apikey: wsApiKey },
			upgrade: false,
			reconnection: true,
			reconnectionAttempts: 2,
			reconnectionDelay: 1000,
			timeout: 20000,
		}

		const setupSources = (socket, sourceName) => {
			const onConnect = () => {}
			const onDisconnect = () => {}
			const onConnectError = (error) => {
				console.error(`Scout websocket connection error (${sourceName})`, {
					error,
					url: websocketUrl,
					path: websocketPath,
				})
			}
			const socketListeners = new Map()

			socket.on('connect', onConnect)
			socket.on('disconnect', onDisconnect)
			socket.on('connect_error', onConnectError)

			SOCKET_EVENTS.forEach((event) => {
				const listener = (payload) => handleSocketEvent(event, payload)
				socketListeners.set(event, listener)
				socket.on(event, listener)
			})

			return () => {
				socket.off('connect', onConnect)
				socket.off('disconnect', onDisconnect)
				socket.off('connect_error', onConnectError)
				SOCKET_EVENTS.forEach((event) => socket.off(event, socketListeners.get(event)))
			}
		}

		const rootSocket = io(websocketUrl, socketOptions)
		const instanceSocket = io(namespaceUrl, socketOptions)
		socketRef.current = rootSocket
		const cleanups = [
			setupSources(rootSocket, 'root'),
			setupSources(instanceSocket, 'instance'),
		]

		return () => {
			cleanups.forEach((cleanup) => cleanup())
			rootSocket.disconnect()
			instanceSocket.disconnect()
			socketRef.current = null
		}
	}, [selectedAccountId, selectedAccount?.instance_name, handleSocketEvent, selectedAccount])

	const scrollThreadToBottom = useCallback(() => {
		window.requestAnimationFrame(() => {
			if (threadRef.current) {
				threadRef.current.scrollTop = threadRef.current.scrollHeight
			}
		})
	}, [])

	const loadConversationMessages = useCallback(async ({ reset = false, cursor = 0, chatMessageId } = {}) => {
		if (!selectedAccountId || !selectedConversationId || (!reset && messagesLoadingRef.current)) {
			return
		}

		const requestVersion = conversationViewVersionRef.current
		const controller = new AbortController()
		const selectedAccountPhone = selectedAccount?.phone_number

		abortManagedRequest(activeThreadRequestRef)
		activeThreadRequestRef.current = { controller, version: requestVersion }

		const ownsRequest = () => {
			return activeThreadRequestRef.current.controller === controller
				&& activeThreadRequestRef.current.version === requestVersion
		}

		const isCurrentRequest = () => {
			return ownsRequest()
				&& conversationViewVersionRef.current === requestVersion
				&& !controller.signal.aborted
		}

		const previousScrollHeight = threadRef.current?.scrollHeight || 0
		const previousScrollTop = threadRef.current?.scrollTop || 0

		messagesLoadingRef.current = true
		setInitialMessagesLoading(reset)
		setMessagesLoading(true)
		
		try {
			const conversationIds = chatMessageId
				? [selectedConversationId]
				: selectedConversationIdsRef.current
			const pendingMessages = listScoutMessages({
				accountId: selectedAccountId,
				conversationId: selectedConversationId,
				conversationIds: conversationIds.length ? conversationIds : [selectedConversationId],
				limit: DEFAULT_MESSAGE_LIMIT,
				skip: cursor,
				sort: '-messageTimestamp',
				slimResponse: 1,
				chatMessageId,
				signal: controller.signal,
			})

			const pendingAnalytics = reset
				? getScoutConversationAnalytics({
					accountId: selectedAccountId,
					conversationId: selectedConversationId,
					date: dateValue,
					signal: controller.signal,
				})
				: Promise.resolve(null)

			const [nextBatch, nextConversationAnalytics] = await Promise.all([pendingMessages, pendingAnalytics])

			if (!isCurrentRequest()) {
				return
			}

			let resolvedBatch = filterThreadMessages(Array.isArray(nextBatch) ? nextBatch : [])

			if (reset && !resolvedBatch.length && selectedAccountPhone) {
				resolvedBatch = await getScoutConversationExport({
					accountId: selectedAccountId,
					date: dateValue,
					conversationId: selectedConversationId,
					signal: controller.signal,
				})

				if (!isCurrentRequest()) {
					return
				}

				resolvedBatch = filterThreadMessages(resolvedBatch)
			}

				if (reset) {
					setMessages((currentMessages) => mergeMessages(resolvedBatch, currentMessages))
					setConversationAnalytics(nextConversationAnalytics)
					setMessageCursor(resolvedBatch.length ? DEFAULT_MESSAGE_LIMIT : -1)
					const historyPreview = getConversationHistoryPreviewFromMessages(resolvedBatch)
					if (historyPreview) {
						setConversations((currentConversations) => currentConversations.map((conversation) => {
							const conversationIds = getConversationIdVariants(conversation)
							if (!conversationIds.includes(selectedConversationId)) {
								return conversation
							}

							return {
								...conversation,
								message: historyPreview,
								message_type: historyPreview.messageType || 'protocolMessage',
								last_message_timestamp: historyPreview.timestamp || conversation.last_message_timestamp,
							}
						}))
					}

					scrollThreadToBottom()
				} else {
				if (threadRef.current && resolvedBatch.length && isCurrentRequest()) {
					pendingHistoryScrollAdjustmentRef.current = {
						previousScrollHeight,
						previousScrollTop,
						version: requestVersion,
					}
				}

				setMessages((currentMessages) => mergeMessages(resolvedBatch, currentMessages))
				setMessageCursor(resolvedBatch.length ? cursor + DEFAULT_MESSAGE_LIMIT : -1)
			}
		} catch (nextError) {
			if (isAbortError(nextError) || !isCurrentRequest()) {
				return
			}
			
		} finally {
			if (conversationViewVersionRef.current === requestVersion && !controller.signal.aborted) {
				messagesLoadingRef.current = false
				setInitialMessagesLoading(false)
				setMessagesLoading(false)
			}

			if (ownsRequest()) {
				activeThreadRequestRef.current.controller = null
			}
		}
	}, [abortManagedRequest, dateValue, scrollThreadToBottom, selectedAccount, selectedAccountId, selectedConversationId])

	useEffect(() => {
		setContact(null)
		setRightSidebar(window.innerWidth >= 600 ? "insights" : null)
		
		if (!selectedAccountId || !selectedConversationId) {
			setMessages([])
			setMessageCursor(0)
			setConversationAnalytics(null)
			return
		}

		setMessages([])
		setMessageCursor(0)
		setConversationAnalytics(null)
		loadConversationMessages({ reset: true, cursor: 0, chatMessageId: anchorMessageId || undefined })

		return () => {
			abortManagedRequest(activeThreadRequestRef)
		}
	}, [abortManagedRequest, anchorMessageId, dateValue, loadConversationMessages, selectedAccountId, selectedConversationId])

	useEffect(() => {
		setPendingNewMessagesCount(0)
		setThreadNearBottom(true)
	}, [selectedConversationId])

	useEffect(() => {
		const nextParams = {}

		if (selectedAccountId) {
			nextParams.accountId = selectedAccountId
		}

		if (selectedConversationId) {
			nextParams.conversationId = selectedConversationId
		}

		if (dateValue) {
			nextParams.date = dateValue
		}

		if (selectedDatePreset) {
			nextParams.period = selectedDatePreset
		}

		if (anchorMessageId) {
			nextParams.anchorMessageId = anchorMessageId
		}

		setSearchParams(nextParams, { replace: true })
	}, [anchorMessageId, dateValue, selectedAccountId, selectedConversationId, selectedDatePreset, setSearchParams])

	const filteredConversations = useMemo(() => {
		return getVisibleConversations(conversations).filter((conversation) => (
			!isIgnoredSelfAddressedProtocolConversation(conversation, selectedAccount?.phone_number)
		))
	}, [conversations, selectedAccount?.phone_number])

	const visibleConversations = useMemo(() => {
		if (conversationCursor === -1) {
			return filteredConversations
		}

		return filteredConversations.slice(0, conversationCursor)
	}, [conversationCursor, filteredConversations])

	const accountByInstanceName = useMemo(() => {
		return accounts.reduce((lookup, account) => {
			if (account?.instance_name) {
				lookup[account.instance_name] = account
			}

			return lookup
		}, {})
	}, [accounts])

	const normalizedSearchResults = useMemo(() => {
		return searchResults.map((entry, index) => {
			const account = accountByInstanceName[entry?.owner] || null
			const conversationId = entry?.info?.id || ''

			return {
				id: `${conversationId || entry?.owner || 'search'}-${index}`,
				conversationId,
				conversationName: entry?.conversationName || 'Conversa monitorada',
				account,
				messages: Array.isArray(entry?.messages) ? entry.messages : [],
			}
		})
	}, [accountByInstanceName, searchResults])

	const selectedConversation = useMemo(() => {
		return findConversationByAnyId(filteredConversations, selectedConversationId)
			|| findConversationByAnyId(conversations, selectedConversationId)
			|| null
	}, [conversations, filteredConversations, selectedConversationId])

	const applyContactNameToConversation = useCallback((targetConversation, value, contactId) => {
		const contactName = normalizeTitleText(value)
		const targetConversationId = targetConversation?.id
		if (!contactName || !targetConversationId) {
			return
		}

		const targetIds = new Set(
			getConversationIdVariants(targetConversation)
				.map((id) => String(id || '').trim())
				.filter(Boolean),
		)
		targetIds.add(String(targetConversationId))

		targetIds.forEach((id) => {
			frozenNamesRef.current.set(id, contactName)
		})

		setConversations((previousConversations) => previousConversations.map((conversation) => {
			const conversationIds = getConversationIdVariants(conversation)
			const isTargetConversation = conversationIds.some((id) => targetIds.has(String(id || '').trim()))

			if (!isTargetConversation) {
				return conversation
			}

			return {
				...conversation,
				name: contactName,
				kwik_contact_id: contactId ? String(contactId) : conversation?.kwik_contact_id,
				kwik_contact_name: contactName,
			}
		}))
	}, [])

	const handleSaveConversationTitle = useCallback(async (targetConversation, value) => {
		const trimmedValue = normalizeTitleText(value)
		if (!trimmedValue || !targetConversation?.phone_num) {
			return
		}

		try {
			let targetContact = targetConversation.id === selectedConversation?.id ? contact : null

			if (!targetContact) {
				targetContact = await getContactData(enterprise?.id, targetConversation.phone_num)
				if (!targetContact) {
					targetContact = await createContact(enterprise?.id, targetConversation)
				}
			}

			await updateContact(enterprise?.id, targetContact?.id, 'name', trimmedValue)

			try {
				await resyncScoutContact(enterprise?.id, selectedAccount?.id, targetConversation.id)
			} catch (syncErr) {
				console.error('Failed to sync contact name to Scout provider:', syncErr)
			}

			if (targetConversation.id === selectedConversation?.id) {
				setContact((prev) => (prev ? { ...prev, name: trimmedValue } : prev))
			}

			applyContactNameToConversation(targetConversation, trimmedValue, targetContact?.id)
			emitSnackbar({ message: 'Nome atualizado com sucesso.', severity: 'success' })
		} catch (err) {
			emitSnackbar({ message: 'Algo deu errado, tente novamente.', severity: 'error' })
		}
	}, [contact, enterprise, selectedAccount, selectedConversation, applyContactNameToConversation])

	const handleOpenSearchConversation = (result, message = null) => {
		if (!result?.conversationId) {
			showSnackbar({ message: 'Não foi possível abrir a conversa a partir deste resultado.', severity: 'warning' })
			return
		}

		const anchorId = message?.id || message?.chatMessageId || message?.messageId
		selectConversation(String(result.conversationId), {
			accountId: result?.account?.id,
			anchorId,
		})

		setSearchResultsOpen(false)
	}

	const handleLoadMediaMessage = useCallback(async (message) => {
		const messageId = getMessageIdentifier(message)

		if (!selectedAccountId || !selectedConversationId || !messageId || loadingMediaIds[messageId]) {
			return
		}

		setLoadingMediaIds((currentState) => ({
			...currentState,
			[messageId]: true,
		}))

		try {
			const fullMessage = await getScoutMessageById({
				accountId: selectedAccountId,
				conversationId: selectedConversationId,
				messageId,
			})

			if (!fullMessage) {
				throw new Error('Não foi possível carregar esta mídia.')
			}

			setMessages((currentMessages) => currentMessages.map((entry, index) => {
				return getMessageIdentifier(entry, index) === messageId
					? { ...entry, ...fullMessage }
					: entry
			}))
		} catch (nextError) {
			showSnackbar({
				message: nextError.message || 'Não foi possível carregar a mídia.',
				severity: 'error',
			})
		} finally {
			setLoadingMediaIds((currentState) => {
				const nextState = { ...currentState }
				delete nextState[messageId]
				return nextState
			})
		}
	}, [loadingMediaIds, selectedAccountId, selectedConversationId, showSnackbar])


	useEffect(() => {
		if (!anchorMessageId || !threadRef.current) {
			return
		}

		const targetMessage = threadRef.current.querySelector(`[data-message-id="${anchorMessageId}"]`)

		if (targetMessage) {
			targetMessage.scrollIntoView({ behavior: 'smooth', block: 'center' })
			setAnchorMessageId('')
			return
		}

		threadRef.current.scrollTop = threadRef.current.scrollHeight
		setAnchorMessageId('')
	}, [anchorMessageId, messages])

	useEffect(() => {
		setLoadingMediaIds({})
	}, [selectedConversationId])

	const totalMessages = (analytics?.conversation_messages || 0) + (analytics?.group_messages || 0)

	const handleBackNavigation = useCallback(() => {
		if (selectedConversationId) {
			selectConversation('')
			return
		}

		navigate('/home')
	}, [navigate, selectConversation, selectedConversationId])

	const accountSummaries = useMemo(() => {
		if (Array.isArray(analytics?.summaries) && analytics.summaries.length) {
			return analytics.summaries
				.filter((item) => item?.summary)
				.map((item, index) => ({
					id: item.id || `summary-${index}`,
					name: item.name || 'Conversa monitorada',
					summary: String(item.summary).trim(),
				}))
		}

		const summarySource = analytics?.summary || analytics?.resumo || analytics?.resumos

		if (Array.isArray(summarySource)) {
			return summarySource
				.map((line, index) => ({
					id: `legacy-summary-${index}`,
					name: 'Resumo',
					summary: String(line).trim(),
				}))
				.filter((item) => item.summary)
		}

		if (typeof summarySource === 'string') {
			return summarySource
				.split(/\n+/)
				.map((line, index) => ({
					id: `legacy-summary-${index}`,
					name: 'Resumo',
					summary: line.trim(),
				}))
				.filter((item) => item.summary)
		}

		return []
	}, [analytics])

	const accountOverviewGraphics = useMemo(() => {
		const viewModel = createHomeGraphicsViewModel({
			analytics,
			selectedAccount,
			useMockData: false,
		})

		if (viewModel.summaries.length) {
			return viewModel
		}

		return {
			...viewModel,
			summaries: accountSummaries.map((item) => ({
				...item,
				tone: 'neutral',
			})),
		}
	}, [accountSummaries, analytics, selectedAccount])

	const insightLines = useMemo(() => {
		const summary = conversationAnalytics?.summary || ''

		if (!summary) {
			return []
		}

		return summary
			.split(/\n+/)
			.map((line) => line.trim())
			.filter(Boolean)
	}, [conversationAnalytics?.summary])

	const renderedMessages = useMemo(() => {
		return messages
			.filter(isRenderableMessage)
			.sort((first, second) => {
				return normalizeComparableTimestamp(first?.timestamp) - normalizeComparableTimestamp(second?.timestamp)
			})
	}, [messages])

	const messageReactionLookup = useMemo(() => {
		return buildMessageReactionLookup(messages)
	}, [messages])

	const [editHistoryEntries, setEditHistoryEntries] = useState([])
	const editHistoryHasDeletion = useMemo(() => {
		return editHistoryEntries.some((entry) => entry?.type === 'DELETED')
	}, [editHistoryEntries])

	const handleOpenEditHistory = useCallback((message) => {
		const history = getEditHistory(message, messages)
		if (history.length) {
			setEditHistoryEntries([message, ...history])
		}
	}, [messages])

	const handleCloseEditHistory = useCallback(() => {
		setEditHistoryEntries([])
	}, [])

	const chatOpen = Boolean(selectedConversationId)
	const exportEmailValid = validateEmail(exportEmail)
	const canExport = Boolean(selectedAccount && selectedConversation && exportEmailValid && exportDateStart && exportDateEnd)
	const overviewMainStyle = !chatOpen
		? {
			padding: '1rem',
			display: 'flex',
			flexDirection: 'column',
			flex: '1 1 auto',
			minHeight: 0,
			overflow: 'auto',
		  }
		: { padding: 0 }
	const overviewGridStyle = !chatOpen
		? {
			flex: '1 1 auto',
			minHeight: 0,
			marginTop: 0,
			alignItems: 'stretch',
		  }
		: undefined
	const overviewPanelStyle = !chatOpen ? { height: '100%', minHeight: 0 } : undefined
	const overviewBodyStyle = !chatOpen ? { minHeight: 0, overflow: 'hidden' } : undefined
	const overviewSummaryListStyle = !chatOpen ? { flex: '1 1 auto', minHeight: 0, overflowX: 'hidden', overflowY: 'auto' } : undefined

	const handleThreadScroll = useCallback(() => {
		if (!threadRef.current || messagesLoading || messageCursor === -1 || suppressThreadScrollHandlerRef.current) {
			return
		}

		const scrollTop = threadRef.current.scrollTop
		const clientHeight = threadRef.current.clientHeight
		const scrollHeight = threadRef.current.scrollHeight
		const distanceFromBottom = scrollHeight - (scrollTop + clientHeight)
		const nearBottom = distanceFromBottom <= 120

		setThreadNearBottom(nearBottom)
		if (nearBottom && pendingNewMessagesCount > 0) {
			setPendingNewMessagesCount(0)
		}

		if (scrollTop <= TOP_LOAD_THRESHOLD) {
			loadConversationMessages({ reset: false, cursor: messageCursor })
		}
	}, [loadConversationMessages, messageCursor, messagesLoading, pendingNewMessagesCount])

	const handleExportConversation = useCallback(async () => {
		if (!canExport || !selectedAccount || !selectedConversation) {
			return
		}

		setExportLoading(true)
		setExportFeedback('')
		let exportAccepted = false

		try {
			const stableTitle = getStableConversationTitle(selectedConversation)
			const chatName = stableTitle !== formatPhoneNumber(selectedConversation?.phone_num) ? `(${stableTitle})` : ''
			const exportRequest = await exportScoutConversationMessages({
				accountId: selectedAccount.id,
				conversationId: selectedConversation.id,
				email: exportEmail.trim(),
				dateStart: exportDateStart,
				dateEnd: exportDateEnd,
				format: exportType,
				numberText: selectedConversation.type === 'GROUP'
					? `para o grupo ${stableTitle}`
					: `para o número ${selectedConversation.phone_num} ${chatName}`,
			})

			exportAccepted = true
			setExportLoading(false)
			setExportDialogOpen(false)
			showSnackbar({
				message: 'Exportação iniciada. O histórico será enviado por e-mail em breve.',
				severity: 'info',
			})

			const statusKey = exportRequest?.key || exportRequest?.status_key || exportRequest?.statusKey
			if (!statusKey) {
				showSnackbar({
					message: 'Exportação iniciada, mas não foi possível acompanhar o envio do e-mail.',
					severity: 'warning',
				})
				return
			}

			const finalStatus = await waitForExportEmailStatus({
				accountId: selectedAccount.id,
				statusKey,
			})

			showSnackbar({
				message: finalStatus?.message || 'Conversa exportada e enviada por e-mail com sucesso.',
				severity: 'success',
			})
		} catch (nextError) {
			if (exportAccepted) {
				showSnackbar({
					message: nextError.message || 'Não foi possível confirmar o envio do e-mail da exportação.',
					severity: 'error',
				})
			} else {
				setExportFeedback(nextError.message)
			}
		} finally {
			setExportLoading(false)
		}
	}, [canExport, exportDateEnd, exportDateStart, exportEmail, exportType, selectedAccount, selectedConversation, getStableConversationTitle, showSnackbar])

	useEffect(() => {
		const loadContact = async () => {
			try {
				let contact = await getContactData(enterprise?.id, selectedConversation?.phone_num)
				if (!contact)
					contact = await createContact(enterprise?.id, selectedConversation)
				setContact(contact)
			} catch (err) {
				emitSnackbar({ message: "Algo deu errado, tente novamente.", severity: "error" })
				setRightSidebar(null)
			}
		}
		if (rightSidebar === "profile" && !contact && selectedConversation?.phone_num && renderCRMFeatures(enterprise))
			loadContact()
	}, [rightSidebar, contact, enterprise, selectedConversation])

	return (
		<div className="scout-page scout-page--conversations">
			{exportDialogOpen ? (
				<DefaultModal
					handleClose={() => setExportDialogOpen(false)}
					title="Exportar conversa"
					actionAlign="right"
					subtitle="A conversa será enviada por e-mail e ficará disponível temporariamente, como no Scout do Kwik."
					action={(
					<>
						<Button onClick={() => setExportDialogOpen(false)}>Cancelar</Button>
						<Button variant="contained" onClick={handleExportConversation} disabled={!canExport || exportLoading}>
							{exportLoading ? 'Enviando...' : 'Enviar'}
						</Button>
					</>
				)}
				maxWidth="sm"
			>
				<div className="scout-export-dialog__grid">
					<TextField
						label="E-mail"
						type="email"
						fullWidth
						value={exportEmail}
						onChange={(event) => setExportEmail(event.target.value)}
						error={Boolean(exportEmail) && !exportEmailValid}
						helperText={Boolean(exportEmail) && !exportEmailValid ? 'Informe um e-mail válido.' : ' '}
					/>
					<TextField
						label="Início"
						type="datetime-local"
						fullWidth
						InputLabelProps={{ shrink: true }}
						value={exportDateStart}
						onChange={(event) => setExportDateStart(event.target.value)}
					/>
					<TextField
						label="Fim"
						type="datetime-local"
						fullWidth
						InputLabelProps={{ shrink: true }}
						value={exportDateEnd}
						onChange={(event) => setExportDateEnd(event.target.value)}
					/>
					<div className="scout-export-format-buttons" style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
						<Button
							variant={exportType === 'pdf' ? 'contained' : 'outlined'}
							onClick={() => setExportType('pdf')}
						>
							PDF
						</Button>
						<Button
							variant={exportType === 'csv' ? 'contained' : 'outlined'}
							onClick={() => setExportType('csv')}
						>
							CSV
						</Button>
					</div>
				</div>
				{exportFeedback ? <p className="scout-inline-feedback scout-inline-feedback--error">{exportFeedback}</p> : null}
			</DefaultModal>
		) : null}

		{Boolean(editHistoryEntries.length) ? (
			<DefaultModal
				open={Boolean(editHistoryEntries.length)}
				handleClose={handleCloseEditHistory}
				title={editHistoryHasDeletion ? 'Mensagem apagada' : 'Histórico de edição'}
				subtitle={editHistoryHasDeletion ? 'Veja a mensagem original e o registro da exclusão' : 'Veja todas as versões desta mensagem'}
				maxWidth="sm"
			>
				<div className="scout-edit-history">
					{editHistoryEntries.map((entry, index) => (
						<div key={`${entry.id || index}-${index}`} className="scout-edit-history__entry">
							<div className="scout-edit-history__meta">
								<span>{index === 0 ? 'Original' : entry.type === 'DELETED' ? 'Apagada em' : entry.type === 'EDITED' ? 'Editado em' : 'Versão'}</span>
								<small>{entry.iso_time || formatTimestampLabel(entry.timestamp)}</small>
							</div>
							<p><LinkifiedText text={formatKwikMessageContent(entry)} /></p>
						</div>
					))}
				</div>
			</DefaultModal>
		) : null}

		{searchResultsOpen ? (
	<SearchResultsModal
		open={searchResultsOpen}
		searchValue={searchValue}
		searchResultsLoading={searchResultsLoading}
		searchFeedback={searchFeedback}
		searchResults={normalizedSearchResults}
		onClose={handleCloseSearchResults}
		onOpenSearchConversation={handleOpenSearchConversation}
	/>
) : null}

		<section className="scout-hero-bar">
				<div>
					<h1 className="scout-hero-bar__title">Olá {user?.first_name || user?.username}, {getGreetings()}!</h1>
					<p className="scout-hero-bar__subtitle">
						{selectedConversation
							? `Visualizando ${getStableConversationTitle(selectedConversation)}`
							: 'Clique em uma conversa para abrir o histórico'}
					</p>
				</div>

				<div className="scout-filter-bar">
					<label className="scout-search-field">
						<SearchIcon />
						<input
							type="search"
							value={searchValue}
							onChange={(event) => setSearchValue(event.target.value)}
							placeholder="Buscar nas conversas"
						/>
					</label>
					<div className="scout-date-dropdown" ref={dateDropdownRef}>
						<button
							type="button"
							className={[
								'scout-date-field',
								'scout-date-field--button',
								isDateMenuOpen ? 'scout-date-field--open' : '',
							].filter(Boolean).join(' ')}
							onClick={() => setIsDateMenuOpen((currentValue) => !currentValue)}
							aria-haspopup="listbox"
							aria-expanded={isDateMenuOpen}
						>
							<span className="scout-date-field__value">{selectedDateLabel}</span>
							<KeyboardArrowDownIcon className={isDateMenuOpen ? 'scout-date-field__chevron scout-date-field__chevron--open' : 'scout-date-field__chevron'} />
						</button>

						{isDateMenuOpen ? (
							<div className="scout-date-dropdown__menu" role="listbox" aria-label="Selecionar período das conversas">
								{baseDateOptions.map((option) => {
									const isActive = option.key === selectedDatePreset

									return (
										<button
											key={option.key || option.value}
											type="button"
											className={isActive ? 'scout-date-dropdown__option scout-date-dropdown__option--active' : 'scout-date-dropdown__option'}
											onClick={() => {
												cancelConversationRequests()
												resetConversationViewState()
												setDateValue(option.endDate || option.value)
												setSelectedDatePreset(option.key || '')
												setIsDateMenuOpen(false)
											}}
											role="option"
											aria-selected={isActive}
										>
											<span>{option.label}</span>
											{isActive ? <span className="scout-date-dropdown__option-indicator" /> : null}
										</button>
									)
								})}
							</div>
						) : null}
					</div>
				</div>
			</section>

			{loading
				? <div className="scout-home-layout">
					<div className="scout-home-loading" aria-live="polite" aria-busy="true">
						<aside className="scout-side-column">
							<section className="scout-panel scout-panel--sidebar scout-loading-panel">
								<div className="scout-panel__heading">
									<span className="scout-loading-chip" />
									<span className="scout-loading-chip" />
								</div>
								<div className="scout-account-list">
									{Array.from({ length: 3 }).map((_, index) => (
										<div key={`account-loading-${index}`} className="scout-account-card scout-loading-block scout-loading-block--account">
											<div className="scout-account-card__avatar scout-loading-orb" />
											<div className="scout-account-card__content">
												<span className="scout-loading-line scout-loading-line--title" />
												<span className="scout-loading-line scout-loading-line--text" />
											</div>
										</div>
									))}
								</div>
							</section>
						</aside>

						<section className="scout-main-column">
							<div className="scout-stats-grid">
								{Array.from({ length: 4 }).map((_, index) => (
									<article key={`stat-loading-${index}`} className="scout-stat-card scout-loading-block">
										<div className="scout-stat-card__header">
											<span className="scout-loading-line scout-loading-line--text scout-loading-line--medium" />
											<span className="scout-loading-dot" />
										</div>
										<div className="scout-stat-card__value-group">
											<span className="scout-loading-line scout-loading-line--value" />
										</div>
									</article>
								))}
							</div>

							<HomeGraphicsSection loading />
						</section>
					</div>
				</div>
				: <div className={chatOpen ? 'scout-conversations-layout scout-conversations-layout--chat-open' : 'scout-conversations-layout'}>
					<ConversationSidebar
						selectedAccount={selectedAccount}
						selectedAccountAvatarClassName={selectedAccountAvatarClassName}
						filteredConversations={visibleConversations}
						selectedConversationId={selectedConversationId}
						selectConversation={selectConversation}
						handleBackNavigation={handleBackNavigation}
						getStableConversationTitle={getStableConversationTitle}
						hasMoreConversations={(conversationCursor !== -1 && visibleConversations.length < filteredConversations.length) || hasMoreRemoteConversations}
						onConversationListScroll={handleConversationListScroll}
						onSaveConversationTitle={handleSaveConversationTitle}
						isEditableContactConversation={isEditableContactConversation}
					/>

					<section className="scout-conversations-main" style={overviewMainStyle}>
						{!chatOpen ? (
							<ConversationOverview
								analytics={analytics}
								enterprise={enterprise}
								totalMessages={totalMessages}
								accountOverviewGraphics={accountOverviewGraphics}
								mobileChartOpen={mobileChartOpen}
								onOpenMobileChart={setMobileChartOpen}
								onCloseMobileChart={() => setMobileChartOpen(null)}
								gridStyle={overviewGridStyle}
								panelStyle={overviewPanelStyle}
								bodyStyle={overviewBodyStyle}
								summaryListStyle={overviewSummaryListStyle}
							/>
						) : (
							<div className={!rightSidebar ? 'scout-thread-layout scout-thread-layout--collapsed' : 'scout-thread-layout'}>
								<ConversationThread
									selectedConversation={selectedConversation}
									selectedAccount={selectedAccount}
									getStableConversationTitle={getStableConversationTitle}
									optionsAnchorEl={optionsAnchorEl}
									optionsOpen={optionsOpen}
									onOpenOptions={(event) => setOptionsAnchorEl(event.currentTarget)}
									onCloseOptions={() => setOptionsAnchorEl(null)}
									onBack={() => selectConversation('')}
									onExportConversation={() => {
										if (renderExportConversation(enterprise)) {
											setExportFeedback('')
											setExportDialogOpen(true)
											setOptionsAnchorEl(null)
										} else {
											showSnackbar({
												message: 'Recurso indisponível no seu plano.',
												severity: 'warning',
											})
										}
									}}
									onShowInsights={() => {
										setRightSidebar('insights')
										setOptionsAnchorEl(null)
									}}
									onShowProfile={() => {
										setRightSidebar('profile')
										setOptionsAnchorEl(null)
									}}
									onSaveConversationTitle={(value) => handleSaveConversationTitle(selectedConversation, value)}
									isEditableContactConversation={isEditableContactConversation}
									enterprise={enterprise}
									initialMessagesLoading={initialMessagesLoading}
									threadRef={threadRef}
									onThreadScroll={handleThreadScroll}
									renderedMessages={renderedMessages}
									messagesLoading={messagesLoading}
									loadingMediaIds={loadingMediaIds}
									onLoadMediaMessage={handleLoadMediaMessage}
									messages={messages}
									searchValue={searchValue}
									onOpenEditHistory={handleOpenEditHistory}
									messageReactionLookup={messageReactionLookup}
								/>

								{rightSidebar === 'insights' ? (
									<ConversationInsightsSidebar
										enterprise={enterprise}
										insightLines={insightLines}
										initialMessagesLoading={initialMessagesLoading}
										conversationAnalytics={conversationAnalytics}
										renderedMessages={renderedMessages}
										onClose={() => setRightSidebar(null)}
									/>
								) : null}

								{rightSidebar === 'profile' ? (
									<ConversationProfileSidebar
										enterprise={enterprise}
										selectedConversation={selectedConversation}
										contact={contact}
										getStableConversationTitle={getStableConversationTitle}
										onClose={() => setRightSidebar(null)}
										onSaveName={(value) => handleSaveConversationTitle(selectedConversation, value)}
										onSaveEnterprise={async (id, name) => {
											try {
												await updateContact(enterprise?.id, contact?.id, 'contact_enterprise', id)
												setContact((prev) => ({ ...prev, contact_enterprise: id, enterprise_name: name }))
												emitSnackbar({ message: 'Empresa atualizada com sucesso.', severity: 'success' })
											} catch (err) {
												emitSnackbar({ message: 'Algo deu errado, tente novamente.', severity: 'error' })
											}
										}}
										onSaveEmail={async (value) => {
											try {
												await updateContact(enterprise?.id, contact?.id, 'email', value)
												setContact((prev) => ({ ...prev, email: value }))
												emitSnackbar({ message: 'E-mail atualizado com sucesso.', severity: 'success' })
											} catch (err) {
												emitSnackbar({ message: 'Algo deu errado, tente novamente.', severity: 'error' })
											}
										}}
										onSaveBirthday={async (value) => {
											try {
												const birthday = value || null
												await updateContact(enterprise?.id, contact?.id, 'birthday', birthday)
												setContact((prev) => ({ ...prev, birthday: birthday ? dayjs(birthday).toISOString() : null }))
												emitSnackbar({ message: 'Aniversário atualizado com sucesso.', severity: 'success' })
											} catch (err) {
												emitSnackbar({ message: 'Algo deu errado, tente novamente.', severity: 'error' })
											}
										}}
										onSaveCpf={async (value) => {
											try {
												await updateContact(enterprise?.id, contact?.id, 'cpf', value)
												setContact((prev) => ({ ...prev, cpf: value }))
												emitSnackbar({ message: 'CPF atualizado com sucesso.', severity: 'success' })
											} catch (err) {
												emitSnackbar({ message: 'Algo deu errado, tente novamente.', severity: 'error' })
											}
										}}
										onSaveCustomerCode={async (value) => {
											try {
												await updateContact(enterprise?.id, contact?.id, 'customer_code', value)
												setContact((prev) => ({ ...prev, customer_code: value }))
												emitSnackbar({ message: 'Código atualizado com sucesso.', severity: 'success' })
											} catch (err) {
												emitSnackbar({ message: 'Algo deu errado, tente novamente.', severity: 'error' })
											}
										}}
									/>
								) : null}
							</div>
						)}

						{loading ? <p className="scout-inline-feedback">Carregando dados...</p> : null}
					</section>
				</div>
			}
		</div>
	)
}

const ConversationsWithBoundary = (props) => (
    <ErrorBoundary
        componentName="Conversations"
        fullScreen
        showReload
        logToBackend
        title="Erro na tela de conversas"
        message="Ocorreu um erro ao carregar as conversas. Tente recarregar a página."
    >
        <Conversations {...props} />
    </ErrorBoundary>
)

export default ConversationsWithBoundary
